
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { formatAppDateTime } from '../../services/appConfigRuntime';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Pagination,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Fab,
  Breadcrumbs,
  Link,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover
} from '@mui/material';
import { Checkbox } from '@mui/material';
import {
  ArrowBack,
  ExpandMore,
  CheckCircle,
  Warning,
  Info,
  SyncAlt,
  Search,
  FilterList,
  Sync,
  Visibility,
  Close,
  ExpandLess,
  Refresh,
  Settings,
  Description,
  ChevronRight,
  Folder,
  FolderOpen,
  MoreVert,
  Error as ErrorIcon,
  HelpOutline,
  Delete,
  History
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { styled } from '@mui/material/styles';
import RunHistoryDialog from '../RunHistoryDialog';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + 'synctrack';
const AUTODESK_DOCS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/autodesk/docs`;

// Styled components matching DynamicContainer structure exactly
const RootDiv = styled('div')({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ContentDiv = styled('div')({
  flexGrow: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  padding: '0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: '0 !important',
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 120px)',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none !important',
  margin: theme.spacing(1),
  '&.MuiPaper-root': {
    borderRadius: '0 !important',
    boxShadow: 'none !important',
  },
  '&': {
    borderRadius: '0 !important',
    boxShadow: 'none !important',
  },
}));

const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const TitleBar = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2),
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
}));

const Title = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontWeight: 600,
}));

// UUID generator compatible with browsers and Node
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const DataPointDetails = () => {
  const { flowInstanceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [vaultData, setVaultData] = useState(null);
  const [fusionData, setFusionData] = useState(null);
  const [unanetData, setUnanetData] = useState(null);
  const [bomData, setBomData] = useState(null);
  const [qboData, setQboData] = useState(null);
  const [autodeskData, setAutodeskData] = useState(null);
  const [autodeskProjectsData, setAutodeskProjectsData] = useState(null);
  const [autodeskDocsData, setAutodeskDocsData] = useState(null);
  const [sourceFolderContents, setSourceFolderContents] = useState(null);
  const [destinationFolderContents, setDestinationFolderContents] = useState(null);
  const [syncRecords, setSyncRecords] = useState([]);
  const [error, setError] = useState(null);

  // Folder expansion state
  const [expandedSourceFolders, setExpandedSourceFolders] = useState({});
  const [expandedTargetFolders, setExpandedTargetFolders] = useState({});
  const [subfolderContents, setSubfolderContents] = useState({});

  // UI State Management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    sourceNumber: true,
    sourceTitle: true,
    sourceRevision: true,
    sourceState: true,
    targetProduct: true,
    targetDescription: true,
    targetVersion: true,
    targetStatus: true,
    targetCategory: true
  });

  // Get data from navigation state
  const { masterFlowId, flowName, tenantId } = location.state || {};

  // Copy Files UI state (moved from SyncFlowParameter.jsx)
  const [projects, setProjects] = useState([]);
  const [sourceFolders, setSourceFolders] = useState([]);
  const [targetFolders, setTargetFolders] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingSourceFolders, setLoadingSourceFolders] = useState(false);
  const [loadingTargetFolders, setLoadingTargetFolders] = useState(false);
  const [selectedSourceProject, setSelectedSourceProject] = useState('');
  const [selectedTargetProject, setSelectedTargetProject] = useState('');
  const [selectedTargetFolder, setSelectedTargetFolder] = useState('');
  const [selectedTargetFolderInfo, setSelectedTargetFolderInfo] = useState(null);
  const [selectedSourceFolders, setSelectedSourceFolders] = useState([]);
  const [folderTree, setFolderTree] = useState({});
  const [loadingFolderChildren, setLoadingFolderChildren] = useState({});
  const [expandedFolders, setExpandedFolders] = useState([]);
  // Refs to track pending fetches to prevent duplicate API calls
  const pendingFolderFetches = useRef(new Set());
  const pendingTargetFolderFetches = useRef(new Set());
  const pendingNestedFetches = useRef(new Set()); // Track nested children fetches
  const [targetFolderTree, setTargetFolderTree] = useState({});
  const [targetExpandedNodes, setTargetExpandedNodes] = useState([]);
  const [targetLoadingNodes, setTargetLoadingNodes] = useState({});
  const [copyMappings, setCopyMappings] = useState([]); // [{srcProjectId, srcProjectName, srcFolderIds[], dstProjectId, dstProjectName, dstFolderId, mappingName?}]
  const [editingIndex, setEditingIndex] = useState(null); // number | null
  const [isRunning, setIsRunning] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [availableHubs, setAvailableHubs] = useState([]);
  const [loadingHubs, setLoadingHubs] = useState(false);
  const [autodeskHubsError, setAutodeskHubsError] = useState(null);
  const [selectedMappingFilter, setSelectedMappingFilter] = useState('all'); // 'all' or '{srcProjectId}|{dstProjectId}'
  const [selectedFolderPairFilter, setSelectedFolderPairFilter] = useState('all'); // 'all' or '{srcProjectId}|{dstProjectId}' for folder structure
  const [copyFilesPage, setCopyFilesPage] = useState(1); // Pagination for Autodesk Docs files table
  const [copyFilesStatusFilter, setCopyFilesStatusFilter] = useState('all'); // 'all' | 'synced' | 'not-synced'
  const [copyFilesSearchTerm, setCopyFilesSearchTerm] = useState(''); // Search term for Autodesk Docs files table
  const COPY_FILES_ROWS_PER_PAGE = 100;

  // Dialog state for adding new mapping
  const [addMappingDialogOpen, setAddMappingDialogOpen] = useState(false);
  const [newMappingAccountId, setNewMappingAccountId] = useState('');
  const [newMappingSourceProjectId, setNewMappingSourceProjectId] = useState('');
  const [newMappingTargetProjectId, setNewMappingTargetProjectId] = useState('');
  const [newMappingSourceProjectName, setNewMappingSourceProjectName] = useState(''); // NEW: Store source project name
  const [newMappingTargetProjectName, setNewMappingTargetProjectName] = useState(''); // NEW: Store target project name
  const [newMappingProjectName, setNewMappingProjectName] = useState('');
  const [newMappingProjectNumber, setNewMappingProjectNumber] = useState('');
  const [newMappingLoading, setNewMappingLoading] = useState(false);
  const [folderContentsByPair, setFolderContentsByPair] = useState({}); // Store folder contents by pair key
  const [dialogProjects, setDialogProjects] = useState([]);
  const [loadingDialogProjects, setLoadingDialogProjects] = useState(false);

  // Delete confirmation dialog state
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [mappingToDeleteIndex, setMappingToDeleteIndex] = useState(null);
  const [isDeletingMapping, setIsDeletingMapping] = useState(false);

  // Compare dialog state
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [comparingMapping, setComparingMapping] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [isComparing, setIsComparing] = useState(false);
  const [mappingName, setMappingName] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'mapping' | 'comparison' | 'tracking'
  const [activeMappingKey, setActiveMappingKey] = useState(null);
  const [activeMapping, setActiveMapping] = useState(null);
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState(null);
  const [rowMenuMapping, setRowMenuMapping] = useState(null);
  const [headerMenuAnchorEl, setHeaderMenuAnchorEl] = useState(null);

  // Folder mapping state for database-driven folder selection
  const [savedFolderMappings, setSavedFolderMappings] = useState([]); // Folders loaded from database
  const [loadingFolderMappings, setLoadingFolderMappings] = useState(false);
  const [savingFolderMappings, setSavingFolderMappings] = useState(false);
  const [selectedFoldersForMapping, setSelectedFoldersForMapping] = useState([]); // Currently selected folders in UI
  const [folderCountsByMapping, setFolderCountsByMapping] = useState({}); // { projectMappingId: count }

  // Message popover state for displaying detailed messages
  const [messagePopoverAnchor, setMessagePopoverAnchor] = useState(null);
  const [messagePopoverContent, setMessagePopoverContent] = useState({ message: '', status: '' });

  // Verification state for Autodesk Copy Files records
  const [verifyStatus, setVerifyStatus] = useState({});

  // Run History state
  const [runHistoryOpen, setRunHistoryOpen] = useState(false);
  const [runHistoryMapping, setRunHistoryMapping] = useState(null);

  // Mapping statistics state (synced, not synced, failed counts, last run)
  const [mappingStatistics, setMappingStatistics] = useState({}); // { 'srcId|dstId': { synced, notSynced, failed, lastRun } }

  const getPairKey = (mapping) => mapping ? `${mapping.srcProjectId}|${mapping.dstProjectId}` : null;

  const mappingsEqual = (a, b) => {
    if (!a || !b) return false;
    return (
      a.srcProjectId === b.srcProjectId &&
      a.dstProjectId === b.dstProjectId &&
      (a.mappingName || '') === (b.mappingName || '')
    );
  };

  const findMappingIndex = (mapping) => {
    if (!mapping) return -1;
    return copyMappings.findIndex(m => mappingsEqual(m, mapping));
  };

  // Function to load project mappings from the new API endpoint
  const loadProjectMappings = async (masterFlowId) => {
    if (!masterFlowId) return null;
    try {
      const token = localStorage.getItem('token');
      const projectMappingsUrl = `${API_BASE_URL}/autodesk/project-mapping/flow/${masterFlowId}`;
      console.log('📡 Loading project mappings from:', projectMappingsUrl);

      const response = await fetch(projectMappingsUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const projectMappings = await response.json();
        console.log('✅ Project mappings loaded:', projectMappings);

        if (Array.isArray(projectMappings) && projectMappings.length > 0) {
          // Transform API response to match copyMappings structure
          const transformedMappings = projectMappings.map(mapping => ({
            aclId: mapping.aclId,
            aclAccountId: mapping.aclAccountId,
            srcProjectId: mapping.aclSrcProjectId,
            dstProjectId: mapping.aclDstProjectId,
            srcProjectName: mapping.aclSrcProjectName || mapping.aclSrcProjectId, // NEW: Use stored project name, fallback to ID
            dstProjectName: mapping.aclDstProjectName || mapping.aclDstProjectId, // NEW: Use stored project name, fallback to ID
            srcFolderIds: [], // Will be populated from other sources if needed
            dstFolderId: null, // Will be populated from other sources if needed
            mappingName: mapping.aclProjectName || null, // Use aclProjectName as mapping name
            isSelected: mapping.aclIsSelected || false
          }));

          console.log('🔄 Transformed mappings:', transformedMappings);
          setCopyMappings(transformedMappings);

          // Load folder counts for each mapping
          await loadFolderCounts(transformedMappings, token);

          // Load mapping statistics
          await loadMappingStatistics(masterFlowId);

          return transformedMappings;
        } else {
          console.log('📋 No project mappings found in response');
          setCopyMappings([]);
          return [];
        }
      } else {
        const errorText = await response.text().catch(() => '');
        console.error('❌ Failed to load project mappings:', response.status, errorText);
        return null;
      }
    } catch (error) {
      console.error('❌ Error loading project mappings:', error);
      return null;
    }
  };

  // Load folder counts for all project mappings
  const loadFolderCounts = async (mappings, token) => {
    if (!mappings || mappings.length === 0) return;

    const counts = {};

    // Load counts in parallel
    await Promise.all(mappings.map(async (mapping) => {
      if (!mapping.aclId) return;

      try {
        const url = `${API_BASE_URL}/autodesk/folder-mapping/project/${mapping.aclId}/selected-count`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const count = await response.json();
          counts[mapping.aclId] = typeof count === 'number' ? count : count.count || 0;
        } else {
          counts[mapping.aclId] = 0;
        }
      } catch (error) {
        console.error(`Error loading folder count for mapping ${mapping.aclId}:`, error);
        counts[mapping.aclId] = 0;
      }
    }));

    setFolderCountsByMapping(counts);
  };

  // Load statistics (synced, not synced, failed, last run) for all mappings
  const loadMappingStatistics = async (masterFlowId) => {
    if (!masterFlowId) return;

    try {
      const token = localStorage.getItem('token');
      const url = `${AUTODESK_DOCS_API_URL}/project-mapping/statistics/${masterFlowId}`;
      console.log('📊 Loading mapping statistics from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Mapping statistics loaded:', data);
        setMappingStatistics(data.statistics || {});
      } else {
        console.error('❌ Failed to load mapping statistics:', response.status);
        setMappingStatistics({});
      }
    } catch (error) {
      console.error('❌ Error loading mapping statistics:', error);
      setMappingStatistics({});
    }
  };

  // Function to load mappings from saved parameters (legacy method - kept for backward compatibility)
  const loadSavedMappings = async (masterFlowId) => {
    if (!masterFlowId) return;
    try {
      const flowParamsUrl = `${API_BASE_URL}/sync-flow-properties/${masterFlowId}`;
      const fpResp = await fetch(flowParamsUrl, {
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (fpResp.ok) {
        const params = await fpResp.json();
        if (Array.isArray(params)) {
          const asMap = new Map();
          params.forEach(p => {
            const name = p.sfpName || p.mfpName || p.name;
            const val = p.sfpValue || p.mfpValue || p.value;
            if (name) asMap.set(name, val);
          });
          // Always check for chunked parts first (they take priority over placeholder)
          const parts = [...asMap.keys()]
            .filter(k => k && k.startsWith('CopyFilesMappings_'))
            .sort((a, b) => {
              try {
                const ia = parseInt(a.substring('CopyFilesMappings_'.length), 10);
                const ib = parseInt(b.substring('CopyFilesMappings_'.length), 10);
                return ia - ib;
              } catch { return a.localeCompare(b); }
            })
            .map(k => asMap.get(k));

          let mappingsJson = null;
          if (parts.length > 0) {
            // Use chunked parts if they exist
            mappingsJson = parts.join('');
          } else {
            // Fall back to single CopyFilesMappings parameter if no chunks
            mappingsJson = asMap.get('CopyFilesMappings');
          }

          // Only set mappings if we have valid JSON (not empty or placeholder)
          if (mappingsJson && mappingsJson.trim().length > 0 && mappingsJson.trim() !== '[]') {
            try {
              const parsed = JSON.parse(mappingsJson);
              if (Array.isArray(parsed) && parsed.length > 0) {
                setCopyMappings(parsed);
                return parsed;
              }
            } catch (e) {
              console.error('Failed to parse CopyFilesMappings:', e);
            }
          }
        }
      }
    } catch (e) {
      console.error('Error loading saved mappings:', e);
    }
    return null;
  };

  // Load mappings when component mounts if it's a copy files flow
  useEffect(() => {
    const loadMappings = async () => {
      if (config?.masterFlowId) {
        const isFilesSync = config.flowName && (config.flowName.toLowerCase().includes('copy') || config.flowName.toLowerCase().includes('file'));
        if (isFilesSync && (!copyMappings || copyMappings.length === 0)) {
          // Try new API first, fallback to legacy if needed
          const projectMappings = await loadProjectMappings(config.masterFlowId);
          if (!projectMappings || projectMappings.length === 0) {
            // Fallback to legacy method if new API returns no data
            await loadSavedMappings(config.masterFlowId);
          }
        }
      }
    };
    loadMappings();
  }, [config?.masterFlowId, config?.flowName]);

  // Auto-select first pair when mappings are loaded and no pair is selected
  useEffect(() => {
    if (copyMappings && copyMappings.length > 0 && selectedFolderPairFilter === 'all') {
      const firstPair = `${copyMappings[0].srcProjectId}|${copyMappings[0].dstProjectId}`;
      setSelectedFolderPairFilter(firstPair);
    }
  }, [copyMappings?.length, selectedFolderPairFilter]);

  // Fetch folder contents when copyMappings are loaded and connector is available
  useEffect(() => {
    const fetchFolderContents = async () => {
      if (copyMappings && copyMappings.length > 0 && config?.masterFlowId) {
        const token = localStorage.getItem('token');
        const autodeskDocsConnector = config?.connectors?.find(c => c.mcName === 'Autodesk Docs');
        const isFilesSync = config?.flowName && (config.flowName.toLowerCase().includes('copy') || config.flowName.toLowerCase().includes('file'));

        if (autodeskDocsConnector && isFilesSync && token) {
          console.log('🔄 Copy mappings loaded, fetching folder contents...');
          await fetchFolderContentsFromSyncRecords([], token, autodeskDocsConnector);

          // Update existing folderContentsByPair with selected folder IDs from mappings
          setFolderContentsByPair(prev => {
            const updated = { ...prev };
            copyMappings.forEach(mapping => {
              const pairKey = `${mapping.srcProjectId}|${mapping.dstProjectId}`;
              if (updated[pairKey] && updated[pairKey].source) {
                updated[pairKey] = {
                  ...updated[pairKey],
                  source: {
                    ...updated[pairKey].source,
                    selectedFolderIds: mapping.srcFolderIds || []
                  }
                };
                console.log(`📋 Updated folder contents for pair ${pairKey} with selected folder IDs:`, mapping.srcFolderIds);
              }
            });
            return updated;
          });
        }
      }
    };

    fetchFolderContents();
  }, [copyMappings?.length, config?.masterFlowId, config?.connectors]);

  // Fetch folder contents when a pair is selected and data is missing
  useEffect(() => {
    const fetchIfNeeded = async () => {
      if (selectedFolderPairFilter !== 'all') {
        const pairData = folderContentsByPair[selectedFolderPairFilter];
        const hasData = pairData &&
          (pairData.source?.data?.length > 0 || pairData.target?.data?.length > 0 ||
            pairData.source?.results?.length > 0 || pairData.target?.results?.length > 0);

        if (!hasData) {
          console.log(`🔄 Pair ${selectedFolderPairFilter} selected but no data, fetching...`);
          // Extract project IDs from the pair key (format: "srcProjectId|dstProjectId")
          const [srcProjectId, dstProjectId] = selectedFolderPairFilter.split('|');
          console.log(`🔍 useEffect: Looking for mapping with:`, {
            srcProjectId,
            dstProjectId,
            copyMappingsCount: copyMappings?.length || 0
          });

          // Try to find mapping in copyMappings first
          let mapping = copyMappings.find(m => {
            const matches = m.srcProjectId === srcProjectId && m.dstProjectId === dstProjectId;
            if (!matches && copyMappings.length > 0) {
              console.log(`  ⚠️ Mapping doesn't match:`, {
                mappingSrc: m.srcProjectId,
                mappingDst: m.dstProjectId,
                expectedSrc: srcProjectId,
                expectedDst: dstProjectId
              });
            }
            return matches;
          });

          // If not found in copyMappings, try to load saved mappings from backend
          if (!mapping && srcProjectId && dstProjectId) {
            console.log(`📝 useEffect: copyMappings not loaded, trying to load saved mappings for this pair...`);
            try {
              const savedMappings = await loadSavedMappings(config?.masterFlowId);
              if (savedMappings && savedMappings.length > 0) {
                mapping = savedMappings.find(m =>
                  m.srcProjectId === srcProjectId && m.dstProjectId === dstProjectId
                );
                if (mapping) {
                  console.log(`✅ Found mapping in saved mappings:`, mapping);
                  // Update copyMappings state with the loaded mappings
                  setCopyMappings(savedMappings);
                }
              }
            } catch (e) {
              console.warn('Could not load saved mappings:', e);
            }

            // If still not found, create a minimal mapping
            if (!mapping) {
              console.log(`📝 Creating temporary mapping from pair key (no saved mappings found)`);
              mapping = {
                srcProjectId,
                dstProjectId,
                srcFolderIds: [], // Will fetch all folders if not specified
                dstFolderId: null
              };
            }
          }

          if (mapping) {
            const token = localStorage.getItem('token');
            const autodeskDocsConnector = config?.connectors?.find(c => c.mcName === 'Autodesk Docs');
            if (autodeskDocsConnector && token) {
              console.log(`📥 useEffect: Fetching folder contents for mapping:`, mapping);
              await fetchFolderContentsForPair(
                mapping.srcProjectId,
                mapping.dstProjectId,
                mapping.srcFolderIds || [],
                mapping.dstFolderId || null,
                token,
                autodeskDocsConnector
              );
            }
          } else {
            console.error(`❌ useEffect: Could not create mapping for pair: ${selectedFolderPairFilter}`, {
              extractedSrc: srcProjectId,
              extractedDst: dstProjectId,
              availableMappings: copyMappings.map(m => ({
                src: m.srcProjectId,
                dst: m.dstProjectId,
                key: `${m.srcProjectId}|${m.dstProjectId}`
              }))
            });
          }
        }
      }
    };

    // Fetch if a pair is selected (even if copyMappings hasn't loaded yet)
    if (selectedFolderPairFilter !== 'all') {
      fetchIfNeeded();
    }
  }, [selectedFolderPairFilter, copyMappings?.length]);

  // Reset mapping filter when copyMappings changes
  useEffect(() => {
    setSelectedMappingFilter('all');
  }, [copyMappings?.length]);

  // Update project names in copyMappings when projects are loaded
  useEffect(() => {
    if (projects.length > 0 && copyMappings.length > 0) {
      const helperGetProjectName = (projectId) => {
        if (!projectId) return projectId;
        const project = projects.find(p => p.id === projectId);
        if (project) {
          const name =
            project.attributes?.name?.name ||
            project.attributes?.name ||
            project.attributes?.displayName ||
            project.displayName ||
            project.name;
          return name && name !== projectId ? name : projectId;
        }
        return projectId;
      };

      const updatedMappings = copyMappings.map(mapping => {
        const srcName = helperGetProjectName(mapping.srcProjectId);
        const dstName = helperGetProjectName(mapping.dstProjectId);

        // Only update if names have changed
        if (mapping.srcProjectName !== srcName || mapping.dstProjectName !== dstName) {
          return {
            ...mapping,
            srcProjectName: srcName,
            dstProjectName: dstName
          };
        }
        return mapping;
      });

      // Check if any mapping was updated
      const hasChanges = updatedMappings.some((m, idx) =>
        m.srcProjectName !== copyMappings[idx].srcProjectName ||
        m.dstProjectName !== copyMappings[idx].dstProjectName
      );

      if (hasChanges) {
        console.log('🔄 Updating project names in mappings:', updatedMappings);
        setCopyMappings(updatedMappings);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, copyMappings.length]);

  useEffect(() => {
    if (flowInstanceId && tenantId) {
      fetchDataPointConfig();
    } else {
      setError('Missing required parameters');
      setLoading(false);
    }
  }, [flowInstanceId, tenantId]);
  // Reset filter to 'all' if it's set to 'target-only' for copy files flows
  useEffect(() => {
    if (config && config.flowName &&
      (config.flowName.toLowerCase().includes('copy') || config.flowName.toLowerCase().includes('file')) &&
      statusFilter === 'target-only') {
      setStatusFilter('all');
    }
  }, [config, statusFilter]);

  // Load projects when account ID changes in dialog (for the add mapping dialog)
  useEffect(() => {
    const loadDialogProjects = async () => {
      if (newMappingAccountId && config?.masterFlowId) {
        const autodeskDocsConnector = config.connectors?.find(c => c.mcName === 'Autodesk Docs');
        if (autodeskDocsConnector) {
          setLoadingDialogProjects(true);
          try {
            const url = `${AUTODESK_DOCS_API_URL}/projects/by-connector?mcId=${autodeskDocsConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&accountId=${encodeURIComponent(newMappingAccountId)}`;
            const response = await fetch(url, {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
              },
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            setDialogProjects(data.results || []);
          } catch (error) {
            console.error('Error loading projects for dialog:', error);
            setDialogProjects([]);
          } finally {
            setLoadingDialogProjects(false);
          }
        }
      } else {
        setDialogProjects([]);
      }
    };
    loadDialogProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newMappingAccountId, config?.masterFlowId]);

  const fetchDataPointConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');

      // Step 1: Get data point configuration (connector IDs)
      const configResponse = await fetch(`${API_BASE_URL}/datapoint/flow/${flowInstanceId}/tenant/${tenantId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!configResponse.ok) {
        throw new Error(`Failed to fetch config: ${configResponse.status}`);
      }

      const configData = await configResponse.json();
      configData.flowIdForConnector = configData.flowInstanceId || configData.masterFlowId;
      console.log('Data Point config:', configData);
      console.log('Connectors found:', configData.connectors);
      setConfig(configData);

      // Step 2: Fetch sync records first (needed for folder contents)
      let records = [];
      if (configData.masterFlowId) {
        records = await fetchSyncRecords(configData.masterFlowId, token);
      }

      // Step 3: Fetch source and target data using connector IDs (and folder contents if needed)
      await fetchSourceAndTargetData(configData, token, records);

      // Initialize Copy Files config UI defaults if applicable
      try {
        const isFilesSync = configData.flowName && (configData.flowName.toLowerCase().includes('copy') || configData.flowName.toLowerCase().includes('file'));
        if (isFilesSync) {
          // Load project mappings from new API endpoint
          const projectMappings = await loadProjectMappings(configData.masterFlowId);
          if (!projectMappings || projectMappings.length === 0) {
            // Fallback to legacy method if new API returns no data
            await loadSavedMappings(configData.masterFlowId);
          }

          const autodeskDocsConnector = configData.connectors?.find(c => c.mcName === 'Autodesk Docs');
          if (autodeskDocsConnector) {
            await fetchHubs(autodeskDocsConnector.mcId, configData.flowIdForConnector, tenantId);
            // Load saved AccountId parameter if any
            const flowParamsUrl = `${API_BASE_URL}/sync-flow-properties/${configData.masterFlowId}`;
            const fpResp = await fetch(flowParamsUrl, { headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (fpResp.ok) {
              const params = await fpResp.json();
              if (Array.isArray(params)) {
                const accountIdParam = params.find(p => (p.sfpName || p.mfpName) === 'AccountId');
                if (accountIdParam?.sfpValue) {
                  setAccountId(accountIdParam.sfpValue);
                  await fetchProjects(autodeskDocsConnector.mcId, accountIdParam.sfpValue);
                }
              }
            }
          }

          // Load saved mappings parameter (if any)
          const flowParamsUrl = `${API_BASE_URL}/sync-flow-properties/${configData.masterFlowId}`;
          const fpResp = await fetch(flowParamsUrl, { headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
          if (fpResp.ok) {
            const params = await fpResp.json();
            if (Array.isArray(params)) {
              const asMap = new Map();
              params.forEach(p => {
                const name = p.sfpName || p.mfpName || p.name;
                const val = p.sfpValue || p.mfpValue || p.value;
                if (name) asMap.set(name, val);
              });
              // Always check for chunked parts first (they take priority over placeholder)
              const parts = [...asMap.keys()]
                .filter(k => k && k.startsWith('CopyFilesMappings_'))
                .sort((a, b) => {
                  try {
                    const ia = parseInt(a.substring('CopyFilesMappings_'.length), 10);
                    const ib = parseInt(b.substring('CopyFilesMappings_'.length), 10);
                    return ia - ib;
                  } catch { return a.localeCompare(b); }
                })
                .map(k => asMap.get(k));

              let mappingsJson = null;
              if (parts.length > 0) {
                // Use chunked parts if they exist
                mappingsJson = parts.join('');
              } else {
                // Fall back to single CopyFilesMappings parameter if no chunks
                mappingsJson = asMap.get('CopyFilesMappings');
              }

              // Only set mappings if we have valid JSON (not empty or placeholder)
              if (mappingsJson && mappingsJson.trim().length > 0 && mappingsJson.trim() !== '[]') {
                try {
                  const parsed = JSON.parse(mappingsJson);
                  if (Array.isArray(parsed) && parsed.length > 0) {
                    setCopyMappings(parsed);
                  }
                } catch (e) {
                  console.error('Failed to parse CopyFilesMappings:', e);
                }
              }
            }
          }
        }
      } catch (e) {
        // noop
      }

    } catch (error) {
      console.error('Error fetching data point:', error);
      setError(`Failed to fetch data: ${error.message}`);
      toast.error(`Failed to fetch data point: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchSourceAndTargetData = async (config, token, records = []) => {
    try {
      console.log('🔍 CONNECTOR DEBUGGING:');
      console.log('  - All connectors available:', config.connectors);
      console.log('  - Flow name:', config.flowName);
      console.log('  - Connectors count:', config.connectors?.length || 0);

      if (config.connectors && config.connectors.length > 0) {
        config.connectors.forEach((connector, index) => {
          console.log(`  - Connector ${index + 1}:`, {
            mcId: connector.mcId,
            mcName: connector.mcName,
            mcRecId: connector.mcRecId
          });
        });
      } else {
        console.warn('⚠️ No connectors found in config!');
      }

      // Helper: match by mc_connector_type (preferred) or mcName so variants (e.g. "Quickbook Production Url") work
      const isQuickBooksConnector = (c) =>
        (c?.mcConnectorType || '').toString().toUpperCase() === 'QUICKBOOKS' ||
        (c?.mcName || '').toLowerCase().includes('quickbook');
      const isAutodeskConnector = (c) =>
        (c?.mcConnectorType || '').toString().toUpperCase() === 'AUTODESK' ||
        (c?.mcName || '') === 'Autodesk';

      // Find all connectors
      const vaultConnector = config.connectors?.find(c => c.mcName === 'Autodesk Vault');
      const fusionConnector = config.connectors?.find(c => c.mcName === 'Fusion Operation');
      const unanetConnector = config.connectors?.find(c => c.mcName === 'Unanet');
      const bomConnector = config.connectors?.find(c => c.mcName === 'BOM' || c.mcName === 'Bom');
      const quickbooksConnector = config.connectors?.find(c => isQuickBooksConnector(c));
      const autodeskConnector = config.connectors?.find(c => isAutodeskConnector(c));
      const autodeskDocsConnector = config.connectors?.find(c => c.mcName === 'Autodesk Docs');

      console.log('🔍 CONNECTOR MATCHING RESULTS:');
      console.log('  - Autodesk Vault connector found:', !!vaultConnector, vaultConnector?.mcName);
      console.log('  - Fusion Operation connector found:', !!fusionConnector, fusionConnector?.mcName);
      console.log('  - Unanet connector found:', !!unanetConnector, unanetConnector?.mcName);
      console.log('  - BOM connector found:', !!bomConnector, bomConnector?.mcName);
      console.log('  - QuickBooks connector found:', !!quickbooksConnector, quickbooksConnector?.mcName);
      console.log('  - Autodesk connector found:', !!autodeskConnector, autodeskConnector?.mcName);
      console.log('  - Autodesk Docs connector found:', !!autodeskDocsConnector, autodeskDocsConnector?.mcName);

      console.log('Autodesk Vault Connector found:', vaultConnector);
      console.log('Fusion Operation Connector found:', fusionConnector);
      console.log('Unanet Connector found:', unanetConnector);
      console.log('BOM Connector found:', bomConnector);
      console.log('QuickBooks Connector found:', quickbooksConnector);
      console.log('Autodesk Connector found:', autodeskConnector);
      console.log('Autodesk Docs Connector found:', autodeskDocsConnector);

      // Determine sync type based on flow name
      const isCustomerSync = config.flowName && config.flowName.toLowerCase().includes('customer');
      const isVendorSync = config.flowName && config.flowName.toLowerCase().includes('vendor');
      const isProductSync = config.flowName && config.flowName.toLowerCase().includes('product');
      const isBomSync = config.flowName && config.flowName.toLowerCase().includes('bom');
      const isProjectSync = config.flowName && config.flowName.toLowerCase().includes('project');
      const isFilesSync = config.flowName && (config.flowName.toLowerCase().includes('copy') || config.flowName.toLowerCase().includes('file'));

      console.log('🔍 SYNC TYPE DETECTION:');
      console.log('  - Flow name:', config.flowName);
      console.log('  - Flow name lowercase:', config.flowName?.toLowerCase());
      console.log('  - Customer sync detected:', isCustomerSync);
      console.log('  - Vendor sync detected:', isVendorSync);
      console.log('  - Product sync detected:', isProductSync);
      console.log('  - BOM sync detected:', isBomSync);
      console.log('  - Project sync detected:', isProjectSync);
      console.log('  - Files/Copy sync detected:', isFilesSync);

      // Debug the detection logic
      console.log('🔍 DETECTION LOGIC DEBUG:');
      console.log('  - includes("customer"):', config.flowName?.toLowerCase().includes('customer'));
      console.log('  - includes("vendor"):', config.flowName?.toLowerCase().includes('vendor'));
      console.log('  - includes("product"):', config.flowName?.toLowerCase().includes('product'));
      console.log('  - includes("bom"):', config.flowName?.toLowerCase().includes('bom'));
      console.log('  - includes("project"):', config.flowName?.toLowerCase().includes('project'));

      // Determine which flowId to use for connector-based APIs (instance first, then master)
      const flowIdForConnector = config.flowIdForConnector || config.flowInstanceId || config.masterFlowId || flowInstanceId;

      // Only fetch data relevant to the sync type

      // Fetch Vault data (only for product/BOM syncs)
      if (vaultConnector && (isProductSync || isBomSync)) {
        const vaultUrl = `${API_BASE_URL}/autodesk/vault/item-versions?mcId=${vaultConnector.mcId}&flowId=${flowIdForConnector}&tenantId=${tenantId}`;
        console.log('Calling Vault API:', vaultUrl);

        const vaultResponse = await fetch(vaultUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('Vault API response status:', vaultResponse.status);

        if (vaultResponse.ok) {
          const vaultResult = await vaultResponse.json();
          console.log('Vault data received:', vaultResult);
          setVaultData(vaultResult);
        } else {
          const errorText = await vaultResponse.text();
          console.error('Failed to fetch Vault data:', vaultResponse.status, errorText);
        }
      } else {
        console.warn('No Vault connector found - cannot fetch Vault data');
      }

      // Fetch Fusion Operation data (only for product syncs)
      if (fusionConnector && isProductSync) {
        const fusionUrl = `${API_BASE_URL}/fusion/products/by-connector?mcId=${fusionConnector.mcId}`;
        console.log('Calling Fusion API:', fusionUrl);

        const fusionResponse = await fetch(fusionUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('Fusion API response status:', fusionResponse.status);

        if (fusionResponse.ok) {
          const fusionResult = await fusionResponse.json();
          console.log('Fusion data received:', fusionResult);
          setFusionData(fusionResult);
        } else {
          const errorText = await fusionResponse.text();
          console.error('Failed to fetch Fusion data:', fusionResponse.status, errorText);
        }
      } else {
        console.warn('No Fusion Operation connector found - cannot fetch Fusion data');
      }

      // Fetch Unanet data (only for product/BOM syncs)
      if (unanetConnector && (isProductSync || isBomSync)) {
        const unanetUrl = `${API_BASE_URL}/unanet/products/by-connector?mcId=${unanetConnector.mcId}&flowId=${flowIdForConnector}&tenantId=${tenantId}`;
        console.log('Calling Unanet API:', unanetUrl);

        const unanetResponse = await fetch(unanetUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('Unanet API response status:', unanetResponse.status);

        if (unanetResponse.ok) {
          const unanetResult = await unanetResponse.json();
          console.log('Unanet data received:', unanetResult);
          setUnanetData(unanetResult);
        } else {
          const errorText = await unanetResponse.text();
          console.error('Failed to fetch Unanet data:', unanetResponse.status, errorText);
        }
      } else {
        console.warn('No Unanet connector found - cannot fetch Unanet data');
      }

      // Fetch BOM data (only for BOM syncs)
      if (bomConnector && isBomSync) {
        const bomUrl = `${API_BASE_URL}/unanet/bom/by-connector?mcId=${bomConnector.mcId}`;
        console.log('Calling BOM API:', bomUrl);

        const bomResponse = await fetch(bomUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('BOM API response status:', bomResponse.status);

        if (bomResponse.ok) {
          const bomResult = await bomResponse.json();
          console.log('BOM data received:', bomResult);
          setBomData(bomResult);
        } else {
          const errorText = await bomResponse.text();
          console.error('Failed to fetch BOM data:', bomResponse.status, errorText);
        }
      } else {
        console.warn('No BOM connector found - cannot fetch BOM data');
      }

      // Fetch QuickBooks customers (only for customer syncs, not project syncs)
      if (quickbooksConnector && isCustomerSync && !isProjectSync) {
        console.log('🔍 CUSTOMER SYNC DETECTED - Calling QuickBooks Customers API');
        const qboUrl = `${API_BASE_URL}/quickbooks/customers/by-connector?mcId=${quickbooksConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}`;
        console.log('Calling QuickBooks Customers API:', qboUrl);

        const qboResponse = await fetch(qboUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('QuickBooks Customers API response status:', qboResponse.status);

        if (qboResponse.ok) {
          const qboResult = await qboResponse.json();
          console.log('QuickBooks customers data received:', qboResult);
          setQboData(qboResult);
        } else {
          const errorText = await qboResponse.text();
          console.error('Failed to fetch QuickBooks customers data:', qboResponse.status, errorText);
        }
      } else {
        console.warn('No QuickBooks connector found for customer sync - cannot fetch QuickBooks data');
      }

      // Fetch QuickBooks vendors (only for vendor syncs)
      if (quickbooksConnector && isVendorSync) {
        console.log('🔍 VENDOR SYNC DETECTED - Calling QuickBooks Vendors API');
        const qboUrl = `${API_BASE_URL}/quickbooks/vendors/by-connector?mcId=${quickbooksConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}`;
        console.log('Calling QuickBooks Vendors API:', qboUrl);

        const qboResponse = await fetch(qboUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('QuickBooks Vendors API response status:', qboResponse.status);

        if (qboResponse.ok) {
          const qboResult = await qboResponse.json();
          console.log('QuickBooks vendors data received:', qboResult);
          console.log('QuickBooks vendors array:', qboResult.Vendor);
          console.log('First vendor sample:', qboResult.Vendor?.[0]);
          setQboData(qboResult);
        } else {
          const errorText = await qboResponse.text();
          console.error('Failed to fetch QuickBooks vendors data:', qboResponse.status, errorText);
        }
      } else {
        console.warn('No QuickBooks connector found for vendor sync - cannot fetch QuickBooks data');
      }

      // Fetch QuickBooks projects (only for project syncs)
      if (quickbooksConnector && isProjectSync) {
        console.log('🔍 PROJECT SYNC DETECTED - Calling QuickBooks Projects API');
        const qboUrl = `${API_BASE_URL}/quickbooks/projects/by-connector?mcId=${quickbooksConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}`;
        console.log('Calling QuickBooks Projects API for project sync:', qboUrl);

        const qboResponse = await fetch(qboUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('QuickBooks Projects API response status:', qboResponse.status);

        if (qboResponse.ok) {
          const qboResult = await qboResponse.json();
          console.log('✅ QuickBooks projects data received for project sync:', qboResult);
          console.log('✅ QuickBooks projects array:', qboResult.Customer);
          console.log('✅ QuickBooks projects count:', qboResult.Customer?.length || 0);
          console.log('✅ First project sample:', qboResult.Customer?.[0]);

          if (!qboResult.Customer || qboResult.Customer.length === 0) {
            console.warn('⚠️ QuickBooks API returned empty projects array - no projects found in QuickBooks');
          }

          setQboData(qboResult);
        } else {
          const errorText = await qboResponse.text();
          console.error('❌ Failed to fetch QuickBooks projects data for project sync:', qboResponse.status, errorText);
          console.error('❌ Error details:', errorText);
        }
      } else if (isProjectSync) {
        console.warn('❌ No QuickBooks connector found for project sync - cannot fetch QuickBooks data');
      }

      // Fetch Autodesk companies (only for customer/vendor syncs, not project syncs)
      if (autodeskConnector && (isCustomerSync || isVendorSync) && !isProjectSync) {
        const autodeskUrl = `${API_BASE_URL}/autodesk/companies/by-connector?mcId=${autodeskConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}`;
        console.log('Calling Autodesk API:', autodeskUrl);

        const autodeskResponse = await fetch(autodeskUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('Autodesk API response status:', autodeskResponse.status);

        if (autodeskResponse.ok) {
          const autodeskResult = await autodeskResponse.json();
          console.log('Autodesk data received:', autodeskResult);
          console.log('Autodesk companies array:', autodeskResult.results);
          console.log('First company sample:', autodeskResult.results?.[0]);
          setAutodeskData(autodeskResult);
        } else {
          const errorText = await autodeskResponse.text();
          console.error('Failed to fetch Autodesk data:', autodeskResponse.status, errorText);
        }
      } else {
        console.warn('No Autodesk connector found - cannot fetch Autodesk data');
      }

      // Fetch Autodesk projects (only for project syncs)
      if (autodeskConnector && isProjectSync) {
        console.log('🔍 PROJECT SYNC DETECTED - Calling Autodesk Projects API');
        const autodeskUrl = `${API_BASE_URL}/autodesk/projects/by-connector?mcId=${autodeskConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}`;
        console.log('Calling Autodesk Projects API:', autodeskUrl);

        const autodeskResponse = await fetch(autodeskUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        console.log('Autodesk Projects API response status:', autodeskResponse.status);

        if (autodeskResponse.ok) {
          const autodeskResult = await autodeskResponse.json();
          console.log('✅ Autodesk projects data received:', autodeskResult);
          console.log('✅ Autodesk projects array:', autodeskResult.results);
          console.log('✅ First project sample:', autodeskResult.results?.[0]);
          setAutodeskProjectsData(autodeskResult);
        } else {
          const errorText = await autodeskResponse.text();
          console.error('❌ Failed to fetch Autodesk projects data:', autodeskResponse.status, errorText);
        }
      } else if (isProjectSync) {
        console.warn('❌ No Autodesk connector found for project sync - cannot fetch Autodesk projects data');
      }

      // Fetch Autodesk Docs folder contents for copy files flow
      if (autodeskDocsConnector && isFilesSync) {
        console.log('🔍 COPY FILES FLOW DETECTED - Fetching folder contents');

        // Always load from flow parameters to ensure initial render shows structure
        // This will fetch folder contents for all mapping pairs
        await fetchFolderContentsFromSyncRecords(records || [], token, autodeskDocsConnector);

        // If folder contents are loaded and a pair is selected, ensure it's displayed
        if (selectedFolderPairFilter !== 'all' && copyMappings.length > 0) {
          const pair = mappingPairs.find(p => p.key === selectedFolderPairFilter);
          if (pair && !folderContentsByPair[selectedFolderPairFilter]) {
            const mapping = copyMappings.find(m =>
              m.srcProjectId === pair.srcProjectId && m.dstProjectId === pair.dstProjectId
            );
            if (mapping) {
              await fetchFolderContentsForPair(
                mapping.srcProjectId,
                mapping.dstProjectId,
                mapping.srcFolderIds,
                mapping.dstFolderId,
                token,
                autodeskDocsConnector
              );
            }
          }
        }
      } else {
        console.log('📋 NOTE: Not a copy files flow - skipping folder contents');
      }

    } catch (error) {
      console.error('Error fetching source/target data:', error);
      toast.warning('Some data could not be loaded');
    }
  };

  // Copy Files configuration helpers (ported)
  const fetchHubs = async (mcId, flowIdParam, tenantIdParam) => {
    if (!mcId) return;
    // Hub / instance flows store connector assignment under (masterFlowId, flowInstanceId); use instance id when present.
    const flowIdForRequest = flowIdParam || config?.flowIdForConnector || config?.masterFlowId;
    const tenantIdValue = tenantIdParam || tenantId;

    if (!flowIdForRequest) {
      console.warn('fetchHubs: No flowId available');
      return;
    }
    if (!tenantIdValue) {
      console.warn('fetchHubs: No tenantId available');
      return;
    }
    setLoadingHubs(true);
    setAutodeskHubsError(null);
    try {
      const url = `${AUTODESK_DOCS_API_URL}/hubs/by-connector?mcId=${mcId}&flowId=${flowIdForRequest}&tenantId=${tenantIdValue}`;
      console.log('Fetching hubs from:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const rawText = await response.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = {};
      }
      const apiMessage = typeof data.message === 'string' ? data.message : null;
      const hasErrorFlag = data.error === true || data.error === 'Connector not assigned to flow';
      if (!response.ok || hasErrorFlag) {
        const msg = apiMessage || (typeof data.error === 'string' ? data.error : null) || `HTTP ${response.status}`;
        console.error('Failed to fetch:', response.status, rawText);
        const userMsg = msg || 'Could not load Autodesk Docs hubs. Check flow connector assignment and connection status.';
        setAutodeskHubsError(userMsg);
        toast.error(userMsg);
        setAvailableHubs([]);
        return;
      }
      console.log('Hubs data received:', data);
      setAutodeskHubsError(null);
      setAvailableHubs(data.results || data.data || []);
    } catch (error) {
      console.error('Error fetching hubs:', error);
      const userMsg = error?.message || 'Could not load Autodesk Docs hubs.';
      setAutodeskHubsError(userMsg);
      toast.error(userMsg);
      setAvailableHubs([]);
    } finally {
      setLoadingHubs(false);
    }
  };

  const fetchProjects = async (mcId, accountIdParam) => {
    if (!mcId || !accountIdParam) {
      setProjects([]);
      return;
    }
    if (!config?.masterFlowId) {
      console.warn('fetchProjects: No masterFlowId available');
      setProjects([]);
      return;
    }
    setLoadingProjects(true);
    try {
      const url = `${AUTODESK_DOCS_API_URL}/projects/by-connector?mcId=${mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&accountId=${encodeURIComponent(accountIdParam)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setProjects(data.results || []);
    } catch (error) {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchFolders = async (mcId, projectId, type) => {
    if (!mcId || !projectId) return;
    if (!config?.masterFlowId) {
      console.warn('fetchFolders: No masterFlowId available');
      return;
    }
    const setLoadingState = type === 'source' ? setLoadingSourceFolders : setLoadingTargetFolders;
    const setFolders = type === 'source' ? setSourceFolders : setTargetFolders;
    setLoadingState(true);
    try {
      const response = await fetch(`${AUTODESK_DOCS_API_URL}/folders/by-project?mcId=${mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&projectId=${projectId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const list = data.results || data.data || [];
      setFolders(list);
      if (type === 'target') {
        setTargetFolderTree({});
        setTargetExpandedNodes([]);
        setTargetLoadingNodes({});
        setSelectedTargetFolder('');
        setSelectedTargetFolderInfo(null);
      }
    } catch (e) {
      setFolders([]);
    } finally {
      setLoadingState(false);
    }
  };

  const fetchFolderChildren = async (mcId, projectId, folderId) => {
    if (!mcId || !projectId || !folderId) return [];
    if (!config?.masterFlowId) {
      console.warn('fetchFolderChildren: No masterFlowId available');
      return [];
    }

    // Guard: Check if already fetched or currently fetching to prevent duplicate calls
    if (folderTree[folderId]) {
      return folderTree[folderId];
    }

    if (pendingFolderFetches.current.has(folderId)) {
      // Already fetching, return empty array (caller should wait for state update)
      return [];
    }

    // Mark as pending
    pendingFolderFetches.current.add(folderId);
    const loadingKey = `${folderId}`;
    setLoadingFolderChildren(prev => ({ ...prev, [loadingKey]: true }));

    try {
      const response = await fetch(`${AUTODESK_DOCS_API_URL}/folders/children?mcId=${mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&projectId=${projectId}&folderId=${folderId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) return [];
      const data = await response.json();
      const children = data.results || data.data || [];
      setFolderTree(prev => ({ ...prev, [folderId]: children }));
      return children;
    } catch (e) {
      return [];
    } finally {
      // Remove from pending set and clear loading state
      pendingFolderFetches.current.delete(folderId);
      setLoadingFolderChildren(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  const fetchTargetFolderChildren = async (mcId, projectId, folderId) => {
    if (!mcId || !projectId || !folderId) return [];
    if (!config?.masterFlowId) {
      console.warn('fetchTargetFolderChildren: No masterFlowId available');
      return [];
    }

    // Guard: Check if already fetched or currently fetching to prevent duplicate calls
    if (targetFolderTree[folderId]) {
      return targetFolderTree[folderId];
    }

    if (pendingTargetFolderFetches.current.has(folderId)) {
      // Already fetching, return empty array (caller should wait for state update)
      return [];
    }

    // Mark as pending
    pendingTargetFolderFetches.current.add(folderId);
    const loadingKey = `${folderId}`;
    setTargetLoadingNodes(prev => ({ ...prev, [loadingKey]: true }));

    try {
      const response = await fetch(`${AUTODESK_DOCS_API_URL}/folders/children?mcId=${mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&projectId=${projectId}&folderId=${folderId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) return [];
      const data = await response.json();
      const children = data.results || data.data || [];
      setTargetFolderTree(prev => ({ ...prev, [folderId]: children }));
      return children;
    } catch (e) {
      return [];
    } finally {
      // Remove from pending set and clear loading state
      pendingTargetFolderFetches.current.delete(folderId);
      setTargetLoadingNodes(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Helper function to recursively fetch all nested children of a folder
  // Returns a map of folderId -> children for all nested levels
  // Includes rate limiting to prevent API quota exceeded errors
  const fetchAllNestedChildren = async (mcId, projectId, folderId) => {
    // Guard: Prevent duplicate nested fetches for the same folder
    if (pendingNestedFetches.current.has(folderId)) {
      // Already fetching nested children for this folder, return empty map
      return {};
    }

    // Mark as pending
    pendingNestedFetches.current.add(folderId);

    try {
      const fetchedMap = {};
      let lastFetchTime = 0;
      const MIN_DELAY_MS = 200; // Minimum delay between API calls (200ms = 5 requests per second max)

      // Helper to throttle API calls
      const throttleFetch = async () => {
        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchTime;
        if (timeSinceLastFetch < MIN_DELAY_MS) {
          await new Promise(resolve => setTimeout(resolve, MIN_DELAY_MS - timeSinceLastFetch));
        }
        lastFetchTime = Date.now();
      };

      const fetchRecursive = async (parentId) => {
        // Check if already in folderTree
        if (folderTree[parentId] && folderTree[parentId].length > 0) {
          fetchedMap[parentId] = folderTree[parentId];
          // Still need to fetch children of these children
          for (const child of folderTree[parentId]) {
            if (!fetchedMap[child.id]) {
              await fetchRecursive(child.id);
            }
          }
        } else {
          // Throttle API calls to prevent rate limiting
          await throttleFetch();

          // Fetch children
          const children = await fetchFolderChildren(mcId, projectId, parentId);
          fetchedMap[parentId] = children || [];

          // Recursively fetch children of each child with throttling
          if (children && children.length > 0) {
            for (const child of children) {
              await fetchRecursive(child.id);
            }
          }
        }
      };

      await fetchRecursive(folderId);

      // Update folderTree with all fetched children
      setFolderTree(prev => {
        const updated = { ...prev };
        Object.keys(fetchedMap).forEach(key => {
          if (!updated[key] || updated[key].length === 0) {
            updated[key] = fetchedMap[key];
          }
        });
        return updated;
      });

      return fetchedMap;
    } finally {
      // Remove from pending set
      pendingNestedFetches.current.delete(folderId);
    }
  };

  // Helper function to recursively get all descendant folder IDs
  const getAllDescendantIds = (folderId, folderTreeData) => {
    const descendants = [];
    const children = folderTreeData[folderId] || [];

    children.forEach(child => {
      descendants.push(child.id);
      // Recursively get descendants of this child
      const childDescendants = getAllDescendantIds(child.id, folderTreeData);
      descendants.push(...childDescendants);
    });

    return descendants;
  };

  // Helper function to count only top-level selected folders (folders without a selected parent)
  const getTopLevelSelectedFoldersCount = (selectedFolders) => {
    const selectedIds = new Set(selectedFolders.map(f => f.id));

    // Filter to only include folders that don't have a selected parent
    return selectedFolders.filter(folder => {
      // If folder has no parent, it's top-level
      if (!folder.parentFolderId) return true;
      // If folder's parent is not selected, it's top-level
      return !selectedIds.has(folder.parentFolderId);
    }).length;
  };

  // Helper function to check if a folder has any selected parent ancestor
  // Since selecting a parent auto-selects all children, we only need to check the direct parent
  // If a grandparent is selected, the parent will also be selected, so checking parent is sufficient
  const hasSelectedParent = (folderId, parentFolderId, selectedFolders) => {
    if (!parentFolderId) return false;

    // Check if the direct parent is selected
    const selectedIds = new Set(selectedFolders.map(f => f.id));
    return selectedIds.has(parentFolderId);
  };

  const handleFolderSelect = async (folderId, folderName, checked, folderPath = null, parentFolderId = null, level = 0, mcId = null, projectId = null) => {
    if (checked) {
      // Use provided path, or fallback to just the folder name
      const path = folderPath || folderName;

      // OPTIMISTIC UPDATE: Immediately add the clicked folder to selection for instant UI feedback
      const clickedFolder = {
        id: folderId,
        name: folderName,
        path: path,
        parentFolderId: parentFolderId,
        level: level
      };

      // Update state immediately (optimistic update)
      setSelectedSourceFolders(prev => {
        const existingIds = new Set(prev.map(f => f.id));
        if (!existingIds.has(folderId)) {
          return [...prev, { id: clickedFolder.id, name: clickedFolder.name, path: clickedFolder.path }];
        }
        return prev;
      });

      setSelectedFoldersForMapping(prev => {
        const existingIds = new Set(prev.map(f => f.id));
        if (!existingIds.has(folderId)) {
          return [...prev, clickedFolder];
        }
        return prev;
      });

      // Now fetch children in the background (non-blocking)
      if (mcId && projectId) {
        // Expand folder if needed (non-blocking)
        if (!expandedFolders.includes(folderId)) {
          setExpandedFolders(prev => [...prev, folderId]);
          if (!folderTree[folderId]) {
            // Fetch children without blocking
            fetchFolderChildren(mcId, projectId, folderId).catch(console.error);
          }
        }

        // Fetch all nested children in the background and update once complete
        (async () => {
          try {
            const allFetchedChildren = await fetchAllNestedChildren(mcId, projectId, folderId);

            // Merge fetched children with current folderTree for getting all descendants
            const mergedFolderTree = { ...folderTree, ...allFetchedChildren };

            // Get all descendant folder IDs to select them automatically
            const descendantIds = getAllDescendantIds(folderId, mergedFolderTree);

            // Build folder objects for all descendants
            const foldersToAdd = [];

            // Add all descendants with their paths - recursively through all levels
            const addDescendants = (parentId, parentPath, parentLevel) => {
              const children = mergedFolderTree[parentId] || [];
              children.forEach(child => {
                const childName = child.attributes?.displayName || child.attributes?.name || child.id;
                const childPath = `${parentPath}/${childName}`;
                foldersToAdd.push({
                  id: child.id,
                  name: childName,
                  path: childPath,
                  parentFolderId: parentId,
                  level: parentLevel + 1
                });
                // Recursively add children of this child (all nested levels)
                addDescendants(child.id, childPath, parentLevel + 1);
              });
            };

            addDescendants(folderId, path, level);

            // Update both legacy state and new mapping state with descendants
            setSelectedSourceFolders(prev => {
              const existingIds = new Set(prev.map(f => f.id));
              const newFolders = foldersToAdd
                .filter(f => !existingIds.has(f.id))
                .map(f => ({ id: f.id, name: f.name, path: f.path }));
              return [...prev, ...newFolders];
            });

            setSelectedFoldersForMapping(prev => {
              const existingIds = new Set(prev.map(f => f.id));
              const newFolders = foldersToAdd.filter(f => !existingIds.has(f.id));
              return [...prev, ...newFolders];
            });
          } catch (error) {
            console.error('Error fetching nested children:', error);
          }
        })();
      }
    } else {
      // When deselecting, also deselect all descendants
      const descendantIds = getAllDescendantIds(folderId, folderTree);
      const idsToRemove = new Set([folderId, ...descendantIds]);

      setSelectedSourceFolders(prev => prev.filter(f => !idsToRemove.has(f.id)));
      setSelectedFoldersForMapping(prev => prev.filter(f => !idsToRemove.has(f.id)));
    }
  };

  const handleFolderExpand = async (mcId, projectId, folderId) => {
    if (!mcId || !projectId) return;
    if (expandedFolders.includes(folderId)) {
      // Collapse immediately
      setExpandedFolders(prev => prev.filter(id => id !== folderId));
    } else {
      // OPTIMISTIC UPDATE: Expand immediately for instant UI feedback
      setExpandedFolders(prev => {
        // Prevent duplicate expansion
        if (prev.includes(folderId)) return prev;
        return [...prev, folderId];
      });

      // Fetch children in the background (non-blocking)
      // fetchFolderChildren now handles duplicate prevention internally
      fetchFolderChildren(mcId, projectId, folderId).catch(error => {
        console.error('Error fetching folder children:', error);
        // Optionally collapse on error
        setExpandedFolders(prev => prev.filter(id => id !== folderId));
      });
    }
  };

  const FolderTreeItem = ({ folder, level = 0, mcId, projectId, parentPath = '', parentFolderId = null }) => {
    const folderId = folder.id;
    const folderName = folder.attributes?.displayName || folder.attributes?.name || folder.id;
    const isExpanded = expandedFolders.includes(folderId);
    const children = folderTree[folderId] || [];
    const isLoading = loadingFolderChildren[folderId];

    // Check selection in the new mapping state (use this for mapping mode)
    const selectedFolders = viewMode === 'mapping' ? selectedFoldersForMapping : selectedSourceFolders;
    const isSelected = selectedFolders.some(f => f.id === folderId);

    // Check if this folder has a selected parent ancestor (should be disabled)
    const hasParentSelected = hasSelectedParent(folderId, parentFolderId, selectedFolders);
    const isDisabled = hasParentSelected;

    // Build full path: parentPath/folderName (or just folderName if root)
    const currentPath = parentPath ? `${parentPath}/${folderName}` : folderName;

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, pl: level * 2, '&:hover': { backgroundColor: 'action.hover' } }}>
          <IconButton size="small" onClick={() => handleFolderExpand(mcId, projectId, folderId)} disabled={isLoading} sx={{ width: 24, height: 24, mr: 0.5 }}>
            {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </IconButton>
          <Checkbox
            checked={isSelected}
            disabled={isDisabled}
            onChange={(e) => {
              if (!isDisabled) {
                handleFolderSelect(folderId, folderName, e.target.checked, currentPath, parentFolderId, level, mcId, projectId);
              }
            }}
            size="small"
            sx={{ p: 0.5 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, ml: 0.5, opacity: isDisabled ? 0.6 : 1 }}>
            {isExpanded ? <FolderOpen fontSize="small" color="primary" sx={{ mr: 1 }} /> : <Folder fontSize="small" sx={{ mr: 1 }} />}
            <Typography variant="body2">{folderName}</Typography>
          </Box>
          {isLoading && (<CircularProgress size={16} sx={{ ml: 1 }} />)}
        </Box>
        {isExpanded && children.length > 0 && (
          <Box>
            {children.map((child) => (
              <FolderTreeItem
                key={child.id}
                folder={child}
                level={level + 1}
                mcId={mcId}
                projectId={projectId}
                parentPath={currentPath}
                parentFolderId={folderId}
              />
            ))}
          </Box>
        )}
        {isExpanded && children.length === 0 && !isLoading && (
          <Box sx={{ pl: level * 2 + 4, py: 0.5 }}>
            <Typography variant="caption" color="textSecondary">No subfolders</Typography>
          </Box>
        )}
      </Box>
    );
  };

  const handleSaveCopyFilesConfiguration = async () => {
    if (!config?.masterFlowId) return;
    if (selectedSourceFolders.length === 0) return;
    try {
      const autodeskDocsConnector = config.connectors?.find(c => c.mcName === 'Autodesk Docs');
      if (!autodeskDocsConnector) return;
      // Build a single mapping from current selection
      // Try multiple ways to extract project name to ensure we always get a readable name
      const getProjectName = (projectId, projectList) => {
        if (!projectId) return null;
        const project = projectList.find(p => p.id === projectId);
        if (!project) return null;

        // Try multiple possible paths for project name
        const name =
          project.attributes?.name?.name ||
          project.attributes?.name ||
          project.attributes?.displayName ||
          project.displayName ||
          project.name ||
          null;

        // Only return name if it's different from the ID (not a fallback)
        return name && name !== projectId ? name : null;
      };

      const srcProjectName = getProjectName(selectedSourceProject, projects) || selectedSourceProject;
      const dstProjectName = getProjectName(selectedTargetProject, projects) || selectedTargetProject;

      // Store folder information with names and paths for display
      const srcFolderInfo = selectedSourceFolders.map(f => ({
        id: f.id,
        name: f.name || f.id,
        path: f.path || f.name || f.id // Store full path
      }));

      const trimmedMappingName = mappingName ? mappingName.trim() : '';
      const effectiveMappingName = trimmedMappingName.length > 0 ? trimmedMappingName : null;

      const mapping = {
        srcProjectId: selectedSourceProject,
        srcProjectName,
        srcFolderIds: selectedSourceFolders.map(f => f.id),
        srcFolderInfo: srcFolderInfo, // Store folder info with names for display
        dstProjectId: selectedTargetProject,
        dstProjectName,
        dstFolderId: selectedTargetFolder,
        dstFolderInfo: selectedTargetFolderInfo ? { ...selectedTargetFolderInfo } : null,
        mappingName: effectiveMappingName,
      };

      const newMappings = editingIndex !== null
        ? copyMappings.map((m, idx) => (idx === editingIndex ? mapping : m))
        : [...copyMappings, mapping];
      setCopyMappings(newMappings);
      const response = await persistCopyMappings(newMappings, autodeskDocsConnector);
      if (!response.ok) throw new Error('Failed to save configuration');
      const displayName = effectiveMappingName || `${srcProjectName} > ${dstProjectName}`;
      toast.success(`${editingIndex !== null ? 'Mapping updated' : 'Mapping saved'}: ${displayName}`);
      // reset current selection for next mapping
      setSelectedSourceProject('');
      setSelectedSourceFolders([]);
      setSourceFolders([]);
      setSelectedTargetProject('');
      setTargetFolders([]);
      setSelectedTargetFolder('');
      setSelectedTargetFolderInfo(null);
      setTargetFolderTree({});
      setTargetExpandedNodes([]);
      setTargetLoadingNodes({});
      setEditingIndex(null);
      setMappingName('');
    } catch (e) {
      toast.error(`Failed to save configuration: ${e.message}`);
    }
  };

  const persistCopyMappings = async (mappings, autodeskDocsConnector) => {
    // Save mappings; if big, chunk across multiple rows (CopyFilesMappings_1..N) to avoid DB length limits
    const jsonString = JSON.stringify(mappings);
    const paramsToSave = [];
    const MAX_CHUNK = 240;
    if (jsonString.length <= MAX_CHUNK) {
      const mappingParam = { sfpName: 'CopyFilesMappings', sfpValue: jsonString, sfpMfId: config.masterFlowId, sfpId: generateUUID() };
      if (autodeskDocsConnector?.mcId) mappingParam.sfpMcId = autodeskDocsConnector.mcId;
      paramsToSave.push(mappingParam);
    } else {
      const chunkSize = MAX_CHUNK;
      let index = 1;
      for (let i = 0; i < jsonString.length; i += chunkSize) {
        const chunk = jsonString.substring(i, i + chunkSize);
        const partParam = { sfpName: `CopyFilesMappings_${index}`, sfpValue: chunk, sfpMfId: config.masterFlowId, sfpId: generateUUID() };
        if (autodeskDocsConnector?.mcId) partParam.sfpMcId = autodeskDocsConnector.mcId;
        paramsToSave.push(partParam);
        index++;
      }
      // Also write a small marker row so controllers that only look at CopyFilesMappings detect intent
      paramsToSave.push({ sfpName: 'CopyFilesMappings', sfpValue: '[]', sfpMfId: config.masterFlowId, sfpId: generateUUID(), ...(autodeskDocsConnector?.mcId ? { sfpMcId: autodeskDocsConnector.mcId } : {}) });
    }
    return fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/sync-flow-properties`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify(paramsToSave),
    });
  };

  const handleDeleteMapping = (index) => {
    // Show confirmation dialog instead of deleting immediately
    setMappingToDeleteIndex(index);
    setDeleteConfirmDialogOpen(true);
  };

  const handleConfirmDeleteMapping = async () => {
    if (mappingToDeleteIndex === null) {
      setDeleteConfirmDialogOpen(false);
      return;
    }

    const index = mappingToDeleteIndex;
    setIsDeletingMapping(true);

    try {
      const mappingToDelete = copyMappings[index];

      // Check if this is a new project mapping (has aclId) or legacy mapping
      if (mappingToDelete?.aclId) {
        // NEW APPROACH: Delete from database via API
        const token = localStorage.getItem('token');
        const deleteUrl = `${API_BASE_URL}/autodesk/project-mapping/${mappingToDelete.aclId}`;

        console.log('🗑️ Deleting project mapping:', mappingToDelete.aclId);

        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to delete mapping: ${response.status}`);
        }

        console.log('✅ Project mapping deleted successfully');
        toast.success('Mapping deleted successfully');

        // Reload project mappings from API to reflect the deletion
        await loadProjectMappings(config.masterFlowId);
        await loadFolderCounts(copyMappings.filter((_, i) => i !== index), token);

        // Refresh sync records to remove deleted mapping's sync records from UI
        if (config?.masterFlowId) {
          const records = await fetchSyncRecords(config.masterFlowId, token);
          setSyncRecords(records);
          console.log('✅ Sync records refreshed after mapping deletion');
        }

      } else {
        // LEGACY APPROACH: For old mappings without aclId
        const autodeskDocsConnector = config.connectors?.find(c => c.mcName === 'Autodesk Docs');
        const newMappings = copyMappings.filter((_, i) => i !== index);
        setCopyMappings(newMappings);
        const resp = await persistCopyMappings(newMappings, autodeskDocsConnector);
        if (!resp.ok) throw new Error('Failed to delete mapping');
        toast.success('Mapping deleted');
      }

      // Close dialog and reset state
      setDeleteConfirmDialogOpen(false);

      // If the deleted mapping was the active one, navigate back to grid
      if (activeMapping && mappingToDelete && mappingsEqual(activeMapping, mappingToDelete)) {
        handleBackToGrid();
      }

      setMappingToDeleteIndex(null);

    } catch (e) {
      console.error('❌ Error deleting mapping:', e);
      toast.error(`Failed to delete mapping: ${e.message}`);
    } finally {
      setIsDeletingMapping(false);
    }
  };

  const handleCancelDeleteMapping = () => {
    setDeleteConfirmDialogOpen(false);
    setMappingToDeleteIndex(null);
  };

  const handleDeleteMappingByReference = (mapping) => {
    const index = findMappingIndex(mapping);
    if (index === -1) {
      toast.error('Could not find mapping to delete');
      return;
    }
    handleDeleteMapping(index);
    // Note: handleBackToGrid will be called after deletion is confirmed in handleConfirmDeleteMapping
  };

  const resetMappingFormState = () => {
    setEditingIndex(null);
    setMappingName('');
    setSelectedSourceProject('');
    setSelectedSourceFolders([]);
    setSourceFolders([]);
    setFolderTree({});
    setExpandedFolders([]);
    setSelectedTargetProject('');
    setTargetFolders([]);
    setSelectedTargetFolder('');
    setSelectedTargetFolderInfo(null);
    setTargetFolderTree({});
    setTargetExpandedNodes([]);
    setTargetLoadingNodes({});
  };

  const handleEditMapping = async (index) => {
    const m = copyMappings[index];
    if (!m) return;
    setEditingIndex(index);
    setMappingName(m.mappingName || '');
    setSelectedSourceProject(m.srcProjectId);
    setSelectedTargetProject(m.dstProjectId);
    setSelectedTargetFolder(m.dstFolderId);
    if (m.dstFolderInfo) {
      setSelectedTargetFolderInfo(m.dstFolderInfo);
    } else if (m.dstFolderId) {
      setSelectedTargetFolderInfo({ id: m.dstFolderId, name: m.dstFolderId, path: m.dstFolderId });
    } else {
      setSelectedTargetFolderInfo(null);
    }
    // Pre-fill selected folders using saved folder info (with names and paths) if available, otherwise use IDs
    if (m.srcFolderInfo && Array.isArray(m.srcFolderInfo) && m.srcFolderInfo.length > 0) {
      setSelectedSourceFolders(m.srcFolderInfo.map(f => ({
        id: f.id,
        name: f.name || f.id,
        path: f.path || f.name || f.id
      })));
    } else {
      setSelectedSourceFolders((m.srcFolderIds || []).map(id => ({ id, name: id, path: id })));
    }
    // Set accountId from mapping if available, otherwise use existing accountId
    if (m.aclAccountId && m.aclAccountId !== accountId) {
      setAccountId(m.aclAccountId);
    }
    // Load projects if accountId is available (needed for dropdown display)
    const autodeskDocsConnector = config.connectors?.find(c => c.mcName === 'Autodesk Docs');
    if (autodeskDocsConnector) {
      const accountIdToUse = m.aclAccountId || accountId;
      if (accountIdToUse) {
        await fetchProjects(autodeskDocsConnector.mcId, accountIdToUse);
      }
      // Load folders for both projects
      if (m.srcProjectId) await fetchFolders(autodeskDocsConnector.mcId, m.srcProjectId, 'source');
      if (m.dstProjectId) await fetchFolders(autodeskDocsConnector.mcId, m.dstProjectId, 'target');
    }
    setTargetFolderTree({});
    setTargetExpandedNodes([]);
    setTargetLoadingNodes({});
  };

  const handleBackToGrid = async () => {
    setViewMode('grid');
    setActiveMapping(null);
    setActiveMappingKey(null);
    setSelectedMappingFilter('all');
    setSelectedFolderPairFilter('all');
    setRowMenuAnchorEl(null);
    setRowMenuMapping(null);
    setHeaderMenuAnchorEl(null);

    // ✅ CRITICAL: Clear ALL folder mapping state to prevent cross-contamination
    setSavedFolderMappings([]);
    setSelectedFoldersForMapping([]);
    setSelectedSourceFolders([]); // ✅ FIX: Also clear legacy selection state
    setSourceFolders([]);
    setFolderTree({});
    setExpandedFolders([]);

    // Reload folder counts to reflect any changes made in the mapping view
    if (copyMappings && copyMappings.length > 0 && config?.masterFlowId) {
      const token = localStorage.getItem('token');
      await loadFolderCounts(copyMappings, token);
    }
  };

  const handleCreateNewMapping = () => {
    // Reset form fields
    setNewMappingAccountId(accountId || '');
    setNewMappingSourceProjectId('');
    setNewMappingTargetProjectId('');
    setNewMappingSourceProjectName(''); // NEW: Reset source project name
    setNewMappingTargetProjectName(''); // NEW: Reset target project name
    setNewMappingProjectName('');
    setNewMappingProjectNumber('');
    // Open dialog
    setAddMappingDialogOpen(true);
  };

  const handleCloseAddMappingDialog = () => {
    setAddMappingDialogOpen(false);
    // Reset form fields
    setNewMappingAccountId('');
    setNewMappingSourceProjectId('');
    setNewMappingTargetProjectId('');
    setNewMappingSourceProjectName(''); // NEW: Reset source project name
    setNewMappingTargetProjectName(''); // NEW: Reset target project name
    setNewMappingProjectName('');
    setNewMappingProjectNumber('');
  };

  const handleSaveNewMapping = async () => {
    if (!config?.masterFlowId) {
      toast.error('Missing flow ID');
      return;
    }
    if (!newMappingAccountId) {
      toast.error('Please select an Account ID');
      return;
    }
    if (!newMappingSourceProjectId) {
      toast.error('Please select a Source Project');
      return;
    }
    if (!newMappingTargetProjectId) {
      toast.error('Please select a Target Project');
      return;
    }

    setNewMappingLoading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        flowId: config.masterFlowId,
        tenantId: tenantId,
        accountId: newMappingAccountId,
        sourceProjectId: newMappingSourceProjectId,
        targetProjectId: newMappingTargetProjectId,
        sourceProjectName: newMappingSourceProjectName || null, // NEW: Send source project name
        targetProjectName: newMappingTargetProjectName || null, // NEW: Send target project name
        projectName: newMappingProjectName || null,
        projectNumber: newMappingProjectNumber || null,
        isSelected: true
      };

      const response = await fetch(`${API_BASE_URL}/autodesk/project-mapping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        // Check if mapping was newly created or already existed
        const isNew = result.isNew !== undefined ? result.isNew : true; // Default to true for backward compatibility
        const message = result.message || (isNew ? 'Mapping created successfully' : 'Mapping already exists');

        if (isNew) {
          toast.success(message);
        } else {
          toast.warning(message);
        }

        handleCloseAddMappingDialog();
        // Reload mappings from API
        await loadProjectMappings(config.masterFlowId);
      } else {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to create mapping: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error creating mapping:', error);
      toast.error(`Failed to create mapping: ${error.message}`);
    } finally {
      setNewMappingLoading(false);
    }
  };

  // Load saved folder mappings from database for a project mapping
  const loadFolderMappings = async (projectMappingId) => {
    if (!projectMappingId) {
      console.warn('No project mapping ID provided');
      setSavedFolderMappings([]);
      setSelectedFoldersForMapping([]); // ✅ FIX: Clear selection state
      return;
    }

    setLoadingFolderMappings(true);
    try {
      const token = localStorage.getItem('token');
      const url = `${API_BASE_URL}/autodesk/folder-mapping/project/${projectMappingId}`;
      console.log('📂 Loading folder mappings from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const folders = await response.json();
        console.log('✅ Folder mappings loaded:', folders);
        setSavedFolderMappings(Array.isArray(folders) ? folders : []);

        // Update selected folders in UI to match saved selections
        const selectedFolders = folders
          .filter(f => f.afmIsSelected)
          .map(f => ({
            id: f.afmFolderId,
            name: f.afmFolderName,
            path: f.afmFolderPath,
            level: f.afmLevel
          }));
        setSelectedFoldersForMapping(selectedFolders);

        return folders;
      } else {
        const errorText = await response.text().catch(() => '');
        console.error('❌ Failed to load folder mappings:', response.status, errorText);
        setSavedFolderMappings([]);
        return [];
      }
    } catch (error) {
      console.error('❌ Error loading folder mappings:', error);
      setSavedFolderMappings([]);
      setSelectedFoldersForMapping([]); // ✅ FIX: Clear selection state on error
      return [];
    } finally {
      setLoadingFolderMappings(false);
    }
  };

  // Save folder selections to database (batch save)
  const saveFolderMappings = async () => {
    if (!activeMapping || !activeMapping.aclId) {
      toast.error('No active project mapping found');
      return false;
    }

    if (selectedFoldersForMapping.length === 0) {
      toast.error('Please select at least one folder');
      return false;
    }

    setSavingFolderMappings(true);
    try {
      const token = localStorage.getItem('token');

      // Prepare payload for batch save
      const payload = {
        projectMappingId: activeMapping.aclId,
        flowId: config.masterFlowId,
        tenantId: tenantId,
        accountId: activeMapping.aclAccountId,
        sourceProjectId: activeMapping.srcProjectId,
        targetProjectId: activeMapping.dstProjectId,
        folders: selectedFoldersForMapping.map(folder => ({
          folderId: folder.id,
          parentFolderId: folder.parentFolderId || null,
          folderName: folder.name,
          folderPath: folder.path,
          isSelected: true,
          level: folder.level || 0
        }))
      };

      console.log('💾 Saving folder mappings:', payload);

      const response = await fetch(`${API_BASE_URL}/autodesk/folder-mapping/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Folder mappings saved:', result);
        toast.success(`${selectedFoldersForMapping.length} folder(s) saved successfully`);

        // Reload saved folder mappings to get updated data with IDs
        await loadFolderMappings(activeMapping.aclId);

        // Reload folder counts to update the grid view
        if (copyMappings && copyMappings.length > 0) {
          await loadFolderCounts(copyMappings, token);
        }

        return true;
      } else {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Failed to save folder mappings: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('❌ Error saving folder mappings:', error);
      toast.error(`Failed to save folder mappings: ${error.message}`);
      return false;
    } finally {
      setSavingFolderMappings(false);
    }
  };

  const handleMappingAction = async (mapping, destination) => {
    if (!mapping) return;
    const pairKey = getPairKey(mapping);
    setActiveMapping(mapping);
    setActiveMappingKey(pairKey);
    setSelectedMappingFilter(pairKey);
    setSelectedFolderPairFilter(pairKey);
    setRowMenuAnchorEl(null);
    setRowMenuMapping(null);
    setHeaderMenuAnchorEl(null);

    if (destination === 'mapping') {
      setViewMode('mapping');

      // ✅ CRITICAL FIX: Clear previous mapping's state FIRST to prevent cross-contamination
      setSavedFolderMappings([]);
      setSelectedFoldersForMapping([]);
      setSelectedSourceFolders([]);
      setSourceFolders([]);
      setFolderTree({});
      setExpandedFolders([]);

      // Load saved folder mappings from database
      if (mapping.aclId) {
        await loadFolderMappings(mapping.aclId);
      }

      // Load source project folders for selection
      const autodeskDocsConnector = config?.connectors?.find(c => c.mcName === 'Autodesk Docs');
      if (autodeskDocsConnector && mapping.srcProjectId) {
        await fetchFolders(autodeskDocsConnector.mcId, mapping.srcProjectId, 'source');
      }

      return;
    }

    setViewMode(destination);
  };

  const handleOpenRowMenu = (event, mapping) => {
    setRowMenuAnchorEl(event.currentTarget);
    setRowMenuMapping(mapping);
  };

  const handleCloseRowMenu = () => {
    setRowMenuAnchorEl(null);
    setRowMenuMapping(null);
  };

  const handleCompareMapping = async (mapping) => {
    if (!mapping || !mapping.aclId) {
      toast.error('Invalid mapping selected for comparison');
      return;
    }

    setComparingMapping(mapping);
    setCompareDialogOpen(true);
    setIsComparing(true);
    setCompareData(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/datapoint/autodesk-copy-files/compare`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          projectMappingId: mapping.aclId,
          flowId: config?.masterFlowId,
          tenantId: config?.tenantId || tenantId,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to compare projects: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      setCompareData(data);
    } catch (error) {
      console.error('❌ Error comparing projects:', error);
      toast.error(`Failed to compare projects: ${error.message}`);
      setCompareData({ error: error.message });
    } finally {
      setIsComparing(false);
      handleCloseRowMenu();
    }
  };

  const handleOpenHeaderMenu = (event) => {
    setHeaderMenuAnchorEl(event.currentTarget);
  };

  const handleCloseHeaderMenu = () => {
    setHeaderMenuAnchorEl(null);
  };

  const handleDeleteRowMapping = () => {
    if (!rowMenuMapping) return;
    const mapping = rowMenuMapping;
    handleCloseRowMenu();
    handleDeleteMappingByReference(mapping);
  };

  const handleDeleteActiveMapping = () => {
    if (!activeMapping) return;
    handleCloseHeaderMenu();
    handleDeleteMappingByReference(activeMapping);
  };

  const handleRunCopyFilesNow = async (specificMapping = null) => {
    if (!config?.masterFlowId) {
      toast.error('Missing flow id');
      return;
    }
    // If a specific mapping was requested but it lacks an ID, avoid falling back to "run all"
    if (specificMapping && !specificMapping.aclId) {
      toast.error('Cannot run this mapping: missing mapping ID. Please reload mappings.');
      return;
    }
    try {
      setIsRunning(true);

      // Check if there are any mappings configured
      if (!copyMappings || copyMappings.length === 0) {
        toast.error('No mappings configured. Please add and save a mapping first.');
        setIsRunning(false);
        return;
      }

      // NEW APPROACH: Mappings are already in the database, no need to persist again
      // The backend will fetch folder selections from autodesk_folder_mapping table
      const projectMappingId = specificMapping?.aclId;
      const mappingDesc = projectMappingId
        ? `for mapping: ${specificMapping.srcProjectName || specificMapping.srcProjectId} → ${specificMapping.dstProjectName || specificMapping.dstProjectId}`
        : 'for all mappings';

      console.log(`🚀 Starting copy files run ${mappingDesc}`, config.masterFlowId);

      // Build URL with optional projectMappingId parameter
      let url = `${process.env.REACT_APP_BACKEND_URL}synctrack/transfer/autodesk-docs-copy-files?flowId=${config.flowIdForConnector || config.masterFlowId}&runType=Manual`;
      if (projectMappingId) {
        url += `&projectMappingId=${projectMappingId}`;
      }

      // IMPORTANT: Do not send tenant headers like `db_name` / `TENANT_DB_PATH`.
      // They are blocked by CORS preflight allow-headers and prevent the request from
      // reaching the backend.
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await resp.json().catch(() => ({}));

      if (resp.ok) {
        const totalFolders = data.totalFolders || 0;
        const successFolders = data.successFolders || 0;
        const failedFolders = data.failedFolders || 0;

        console.log('✅ Copy files run completed:', data);

        if (totalFolders === 0) {
          toast.warning('No folders selected for copying. Please select folders in the Mapping page.');
        } else if (failedFolders === 0) {
          toast.success(`Copy completed successfully! ${successFolders}/${totalFolders} folders processed.`);
        } else {
          toast.warning(`Copy completed with errors: ${successFolders}/${totalFolders} successful, ${failedFolders} failed.`);
        }

        // Refresh sync records to show new copy results
        if (config?.masterFlowId) {
          const token = localStorage.getItem('token');
          await fetchSyncRecords(config.masterFlowId, token);
          // Reload mappings from database to ensure they remain visible
          await loadProjectMappings(config.masterFlowId);
        }
      } else {
        console.error('❌ Copy files run failed:', data);
        toast.error(`Run failed: ${data.message || resp.status}`);
      }
    } catch (e) {
      console.error('❌ Run error:', e);
      toast.error(`Run error: ${e.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  const fetchSyncRecords = async (masterFlowId, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/datapoint/sync-records/${masterFlowId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Sync records:', result);
        const records = result.records || [];
        setSyncRecords(records);
        return records;
      }
      return [];
    } catch (error) {
      console.warn('Could not fetch sync records:', error);
      return [];
    }
  };

  // Fetch folder contents for a specific mapping pair
  const fetchFolderContentsForPair = async (srcProjectId, dstProjectId, srcFolderIds, dstFolderId, token, autodeskDocsConnector) => {
    const pairKey = `${srcProjectId}|${dstProjectId}`;
    console.log(`📁 Fetching folder contents for pair: ${pairKey}`, {
      srcProjectId,
      dstProjectId,
      srcFolderIds,
      dstFolderId,
      connectorId: autodeskDocsConnector?.mcId
    });

    try {
      let sourceData = null;
      let targetData = null;

      // Fetch source project folders
      if (srcProjectId) {
        if (!config?.masterFlowId) {
          console.warn('fetchFolderContentsForPair: No masterFlowId available');
          return { source: null, target: null };
        }
        const sourceFoldersUrl = `${API_BASE_URL}/autodesk/docs/folders/by-project?mcId=${autodeskDocsConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&projectId=${encodeURIComponent(srcProjectId)}`;
        console.log(`📁 Fetching source folders from: ${sourceFoldersUrl}`);

        const sourceFoldersResponse = await fetch(sourceFoldersUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (sourceFoldersResponse.ok) {
          const sourceFoldersData = await sourceFoldersResponse.json();
          console.log(`✅ Source folders response:`, sourceFoldersData);

          const foldersList = sourceFoldersData.data || sourceFoldersData.results || sourceFoldersData || [];
          console.log(`📊 Source folders list (${foldersList.length} items):`, foldersList);

          sourceData = {
            data: foldersList,
            results: foldersList,
            count: foldersList.length,
            projectId: srcProjectId,
            selectedFolderIds: srcFolderIds || []
          };
        } else {
          const errorText = await sourceFoldersResponse.text();
          console.error(`❌ Failed to fetch source folders: ${sourceFoldersResponse.status}`, errorText);
        }
      }

      // Fetch target project folders
      if (dstProjectId) {
        if (!config?.masterFlowId) {
          console.warn('fetchFolderContentsForPair: No masterFlowId available');
          return { source: null, target: null };
        }
        const targetFoldersUrl = `${API_BASE_URL}/autodesk/docs/folders/by-project?mcId=${autodeskDocsConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&projectId=${encodeURIComponent(dstProjectId)}`;
        console.log(`📁 Fetching target folders from: ${targetFoldersUrl}`);

        const targetFoldersResponse = await fetch(targetFoldersUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (targetFoldersResponse.ok) {
          const targetFoldersData = await targetFoldersResponse.json();
          console.log(`✅ Target folders response:`, targetFoldersData);

          const foldersList = targetFoldersData.data || targetFoldersData.results || targetFoldersData || [];
          console.log(`📊 Target folders list (${foldersList.length} items):`, foldersList);

          targetData = {
            data: foldersList,
            results: foldersList,
            count: foldersList.length,
            projectId: dstProjectId,
            targetFolderId: dstFolderId
          };
        } else {
          const errorText = await targetFoldersResponse.text();
          console.error(`❌ Failed to fetch target folders: ${targetFoldersResponse.status}`, errorText);
        }
      }

      // Store folder contents by pair key
      console.log(`💾 Storing folder contents for pair ${pairKey}:`, {
        sourceCount: sourceData?.data?.length || 0,
        targetCount: targetData?.data?.length || 0
      });

      setFolderContentsByPair(prev => ({
        ...prev,
        [pairKey]: {
          source: sourceData,
          target: targetData
        }
      }));

      return { source: sourceData, target: targetData };
    } catch (error) {
      console.error(`❌ Error fetching folder contents for pair ${pairKey}:`, error);
      return { source: null, target: null };
    }
  };

  const fetchFolderContentsFromSyncRecords = async (records, token, autodeskDocsConnector) => {
    try {
      console.log('📁 Fetching folder contents - attempting to extract from flow parameters...');

      if (!autodeskDocsConnector) {
        console.warn('Could not find Autodesk Docs connector for folder contents');
        return;
      }

      if (!config || !config.masterFlowId) {
        console.warn('Config or masterFlowId not available');
        return;
      }

      // Fetch flow parameters to get folder IDs
      try {
        const flowParamsUrl = `${API_BASE_URL}/sync-flow-properties/${config.masterFlowId}`;
        console.log('📁 Fetching flow properties:', flowParamsUrl);

        const flowParamsResponse = await fetch(flowParamsUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (flowParamsResponse.ok) {
          const flowParamsResponseData = await flowParamsResponse.json();
          console.log('✅ Flow properties received:', flowParamsResponseData);

          // Convert array to map for easier access
          const flowParamsMap = {};
          let flowParamsArray = null;

          // Handle both array and map responses
          if (Array.isArray(flowParamsResponseData)) {
            flowParamsArray = flowParamsResponseData;
          } else if (typeof flowParamsResponseData === 'object') {
            // If it's already a map, use it directly
            Object.assign(flowParamsMap, flowParamsResponseData);
            flowParamsArray = Object.entries(flowParamsResponseData).map(([key, value]) => ({ name: key, value: value }));
          }

          if (Array.isArray(flowParamsArray)) {
            flowParamsArray.forEach(param => {
              // Try both possible field names
              const name = param.sfpName || param.mfpName || param.name || param.parameterName || param.key;
              // Try all possible value field names
              const value = param.sfpValue || param.mfpValue || param.value || param.parameterValue || param.paramValue || param.mfpValueText || param.text;
              flowParamsMap[name] = value;
              console.log(`📋 Flow property: ${name} = ${value}`);
              console.log(`   Available fields:`, Object.keys(param));
              console.log(`   Full object:`, param);
            });
          }

          console.log('📁 Flow parameters map:', flowParamsMap);
          console.log('📁 Available parameter names:', Object.keys(flowParamsMap));

          // Store flowParamsMap in window for use in renderFolderContents
          window.flowParamsMap = flowParamsMap;

          // Fetch folder contents for all mapping pairs
          if (copyMappings && copyMappings.length > 0) {
            console.log('📁 Fetching folder contents for all mapping pairs:', copyMappings.length);

            // Fetch folder contents for each mapping pair
            for (const mapping of copyMappings) {
              await fetchFolderContentsForPair(
                mapping.srcProjectId,
                mapping.dstProjectId,
                mapping.srcFolderIds,
                mapping.dstFolderId,
                token,
                autodeskDocsConnector
              );
            }

            // If no pair is selected, show first pair by default
            if (selectedFolderPairFilter === 'all' && copyMappings.length > 0) {
              const firstPair = `${copyMappings[0].srcProjectId}|${copyMappings[0].dstProjectId}`;
              setSelectedFolderPairFilter(firstPair);
            }
          } else {
            // Legacy: Extract folder IDs from flow parameters (try different naming conventions)
            const sourceProjectId = flowParamsMap.srcProjectId || flowParamsMap.src_project_id || flowParamsMap.sourceProjectId || flowParamsMap.source_project_id;
            const sourceFolderId = flowParamsMap.srcFolderId || flowParamsMap.src_folder_id || flowParamsMap.sourceFolderId || flowParamsMap.source_folder_id;
            const targetProjectId = flowParamsMap.dstProjectId || flowParamsMap.dst_project_id || flowParamsMap.targetProjectId || flowParamsMap.target_project_id;
            const targetFolderId = flowParamsMap.dstFolderId || flowParamsMap.dst_folder_id || flowParamsMap.targetFolderId || flowParamsMap.target_folder_id;

            console.log('📁 Folder IDs extracted:', {
              sourceProjectId,
              sourceFolderId,
              targetProjectId,
              targetFolderId
            });

            // Fetch all top-level folders from source project (full project structure)
            // This shows the complete folder structure regardless of selected folders for copying
            if (sourceProjectId) {
              if (!config?.masterFlowId) {
                console.warn('fetchFolderContentsFromSyncRecords: No masterFlowId available');
                return;
              }
              // Use the folders endpoint to get all top-level folders from the project
              const sourceFoldersUrl = `${API_BASE_URL}/autodesk/docs/folders/by-project?mcId=${autodeskDocsConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&projectId=${encodeURIComponent(sourceProjectId)}`;
              console.log('📁 Fetching all source project folders (full structure):', sourceFoldersUrl);

              const sourceFoldersResponse = await fetch(sourceFoldersUrl, {
                method: 'GET',
                headers: {
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
              });

              if (sourceFoldersResponse.ok) {
                const sourceFoldersData = await sourceFoldersResponse.json();
                console.log('✅ Source project folders received:', sourceFoldersData);
                console.log('📊 Source folders structure:', {
                  hasData: !!sourceFoldersData.data,
                  hasResults: !!sourceFoldersData.results,
                  dataLength: sourceFoldersData.data?.length || sourceFoldersData.results?.length,
                  fullResponse: sourceFoldersData
                });

                // Transform the folders response to match the expected structure
                // The folders API returns { results: [...] } or { data: [...] }
                const foldersList = sourceFoldersData.data || sourceFoldersData.results || [];
                const transformedSourceData = {
                  data: foldersList,
                  results: foldersList,
                  count: foldersList.length
                };

                // Store selected folder IDs to highlight them in the UI
                const selectedSourceFolderIds = sourceFolderId ? sourceFolderId.split(',').map(id => id.trim()) : [];
                window.selectedSourceFolderIds = selectedSourceFolderIds;
                console.log('📋 Selected source folder IDs for highlighting:', selectedSourceFolderIds);

                setSourceFolderContents(transformedSourceData);
              } else {
                const errorText = await sourceFoldersResponse.text();
                console.error('❌ Failed to fetch source project folders:', sourceFoldersResponse.status, errorText);

                // Fallback: if folders API fails, try to fetch the selected folder contents
                if (sourceFolderId) {
                  if (!config?.masterFlowId) {
                    console.warn('fetchFolderContentsFromSyncRecords: No masterFlowId available for fallback');
                    return;
                  }
                  console.log('📁 Falling back to selected folder contents:', sourceFolderId);
                  const sourceUrl = `${API_BASE_URL}/autodesk/docs/source-folder-contents/by-connector?mcId=${autodeskDocsConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&projectId=${encodeURIComponent(sourceProjectId)}&folderId=${encodeURIComponent(sourceFolderId.split(',')[0].trim())}`;
                  const sourceResponse = await fetch(sourceUrl, {
                    method: 'GET',
                    headers: {
                      'Accept': 'application/json',
                      'Authorization': `Bearer ${token}`,
                    },
                  });

                  if (sourceResponse.ok) {
                    const sourceData = await sourceResponse.json();
                    setSourceFolderContents(sourceData);
                  }
                }
              }
            }

            // Fetch all top-level folders from target project (full project structure)
            if (targetProjectId) {
              if (!config?.masterFlowId) {
                console.warn('fetchFolderContentsFromSyncRecords: No masterFlowId available');
                return;
              }
              // Use the folders endpoint to get all top-level folders from the project
              const targetFoldersUrl = `${API_BASE_URL}/autodesk/docs/folders/by-project?mcId=${autodeskDocsConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&projectId=${encodeURIComponent(targetProjectId)}`;
              console.log('📁 Fetching all target project folders (full structure):', targetFoldersUrl);

              const targetFoldersResponse = await fetch(targetFoldersUrl, {
                method: 'GET',
                headers: {
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
              });

              if (targetFoldersResponse.ok) {
                const targetFoldersData = await targetFoldersResponse.json();
                console.log('✅ Target project folders received:', targetFoldersData);
                console.log('📊 Target folders structure:', {
                  hasData: !!targetFoldersData.data,
                  hasResults: !!targetFoldersData.results,
                  dataLength: targetFoldersData.data?.length || targetFoldersData.results?.length,
                  fullResponse: targetFoldersData
                });

                // Transform the folders response to match the expected structure
                const foldersList = targetFoldersData.data || targetFoldersData.results || [];
                const transformedTargetData = {
                  data: foldersList,
                  results: foldersList,
                  count: foldersList.length
                };

                // Legacy: Set destination folder contents (for backward compatibility)
                setDestinationFolderContents(transformedTargetData);
              } else {
                const errorText = await targetFoldersResponse.text();
                console.error('❌ Failed to fetch target project folders:', targetFoldersResponse.status, errorText);

                // Fallback: if folders API fails, try to fetch the target folder contents
                if (targetFolderId) {
                  if (!config?.masterFlowId) {
                    console.warn('fetchFolderContentsFromSyncRecords: No masterFlowId available for fallback');
                    return;
                  }
                  console.log('📁 Falling back to target folder contents:', targetFolderId);
                  const targetUrl = `${API_BASE_URL}/autodesk/docs/target-folder-contents/by-connector?mcId=${autodeskDocsConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&projectId=${encodeURIComponent(targetProjectId)}&folderId=${encodeURIComponent(targetFolderId)}`;
                  const targetResponse = await fetch(targetUrl, {
                    method: 'GET',
                    headers: {
                      'Accept': 'application/json',
                      'Authorization': `Bearer ${token}`,
                    },
                  });

                  if (targetResponse.ok) {
                    const targetData = await targetResponse.json();
                    setDestinationFolderContents(targetData);
                  }
                }
              }
            }
          }
        } else {
          console.error('❌ Failed to fetch flow properties:', flowParamsResponse.status);
        }
      } catch (error) {
        console.error('❌ Error fetching flow parameters:', error);
      }

    } catch (error) {
      console.error('❌ Error fetching folder contents:', error);
    }
  };

  // Fetch subfolder contents when a folder is expanded
  const fetchSubfolderContents = async (folderId, projectId, isSource) => {
    const token = localStorage.getItem('token');

    // Find the Autodesk Docs connector
    const autodeskDocsConnector = config?.connectors?.find(c => c.mcName === 'Autodesk Docs');
    if (!autodeskDocsConnector) {
      console.warn('Autodesk Docs connector not found');
      return;
    }

    if (!config?.masterFlowId) {
      console.warn('fetchSubfolderContents: No masterFlowId available');
      return;
    }

    try {
      const url = isSource
        ? `${API_BASE_URL}/autodesk/docs/source-folder-contents/by-connector?mcId=${autodeskDocsConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&projectId=${encodeURIComponent(projectId)}&folderId=${encodeURIComponent(folderId)}`
        : `${API_BASE_URL}/autodesk/docs/target-folder-contents/by-connector?mcId=${autodeskDocsConnector.mcId}&flowId=${config.flowIdForConnector || config.masterFlowId}&projectId=${encodeURIComponent(projectId)}&folderId=${encodeURIComponent(folderId)}`;

      console.log('📂 Fetching subfolder contents:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Subfolder contents received:', data);

        // Store subfolder contents with folderId as key
        setSubfolderContents(prev => ({
          ...prev,
          [folderId]: data
        }));
      } else {
        console.error('❌ Failed to fetch subfolder contents:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching subfolder contents:', error);
    }
  };

  const handleBack = () => {
    navigate('/sync-flow');
  };

  // Helper function to clean up technical URN messages for user-friendly display
  const cleanupMessage = (message, status) => {
    if (!message || message.trim() === '') {
      return '-';
    }

    // If message looks like a URN (version:urn:adsk...), extract meaningful info
    if (message.startsWith('version:urn:adsk')) {
      // Extract version number from URN
      const versionMatch = message.match(/version=(\d+)/);
      if (versionMatch) {
        return `File version ${versionMatch[1]} synced successfully`;
      }
      return 'Synced successfully';
    }

    // If message contains "already integrated" or similar, make it friendlier
    if (message.toLowerCase().includes('already integrated') ||
      message.toLowerCase().includes('skipped on re-run')) {
      return 'Already synced (up to date)';
    }

    if (message.toLowerCase().includes('already copied to destination') ||
      message.toLowerCase().includes('already copied to this destination')) {
      return 'Already exists in destination';
    }

    // For error messages, keep them as-is but ensure they're readable
    if (status?.toLowerCase() === 'failed' || status?.toLowerCase() === 'error') {
      return message;
    }

    // For other skipped messages
    if (status?.toLowerCase() === 'skipped' && message.length > 100) {
      // Truncate long technical messages
      return message.substring(0, 97) + '...';
    }

    // Return as-is for other cases
    return message;
  };

  // Helper function to intelligently truncate long file paths
  const truncatePath = (path, maxLength = 50) => {
    if (!path || path.length <= maxLength) {
      return path;
    }

    // Split the path by '/'
    const parts = path.split('/');

    if (parts.length <= 2) {
      // If path has only 1-2 parts, just truncate from the middle
      const start = path.substring(0, 20);
      const end = path.substring(path.length - 20);
      return `${start}...${end}`;
    }

    // Show first part (root) and last part (filename)
    const firstPart = parts[0];
    const lastPart = parts[parts.length - 1];

    // If first + last is already too long, truncate the filename
    if ((firstPart + lastPart).length > maxLength) {
      const filenameTruncated = lastPart.length > 30
        ? lastPart.substring(0, 15) + '...' + lastPart.substring(lastPart.length - 12)
        : lastPart;
      return `${firstPart}/.../${filenameTruncated}`;
    }

    // Show root/.../filename format
    return `${firstPart}/.../${lastPart}`;
  };

  // Merge source and target data for side-by-side comparison
  const mergedData = useMemo(() => {
    const vaultItems = vaultData?.results || [];
    const fusionProducts = fusionData?.results || [];
    const unanetProducts = unanetData?.results || [];
    const bomItems = bomData?.results || [];
    const qboCustomers = qboData?.customers || [];
    const qboVendors = qboData?.Vendor || [];  // Note: Capital 'V' - QuickBooks API returns 'Vendor'
    const autodeskCompanies = autodeskData?.results || [];
    const autodeskDocsFiles = autodeskDocsData?.results || [];

    const merged = [];

    // For Vault to Unanet Product Sync
    if (vaultItems.length > 0 && unanetProducts.length > 0) {
      const matchedUnanetIds = new Set();

      // Match Vault items with Unanet products
      vaultItems.forEach(vaultItem => {
        const matchKey = vaultItem.number || vaultItem.name;
        const matchedUnanet = unanetProducts.find(u =>
          u.barcode === matchKey ||
          u.product === matchKey
        );

        if (matchedUnanet) {
          matchedUnanetIds.add(matchedUnanet.product_id);
          merged.push({
            type: 'vault-unanet',
            status: 'matched',
            source: vaultItem,
            target: matchedUnanet,
            matchKey: matchKey
          });
        } else {
          merged.push({
            type: 'vault-unanet',
            status: 'source-only',
            source: vaultItem,
            target: null,
            matchKey: matchKey
          });
        }
      });

      // Find Unanet products not matched to any Vault item
      unanetProducts.forEach(unanetItem => {
        if (!matchedUnanetIds.has(unanetItem.product_id)) {
          merged.push({
            type: 'vault-unanet',
            status: 'target-only',
            source: null,
            target: unanetItem,
            matchKey: unanetItem.barcode || unanetItem.product
          });
        }
      });
    }

    // For Vault to Fusion Operation Sync
    if (vaultItems.length > 0 && fusionProducts.length > 0) {
      const matchedFusionIds = new Set();

      vaultItems.forEach(vaultItem => {
        const matchKey = vaultItem.number || vaultItem.name;
        const matchedFusion = fusionProducts.find(f =>
          f.code === matchKey ||
          f.name === matchKey
        );

        if (matchedFusion) {
          matchedFusionIds.add(matchedFusion.id);
          merged.push({
            type: 'vault-fusion',
            status: 'matched',
            source: vaultItem,
            target: matchedFusion,
            matchKey: matchKey
          });
        } else {
          merged.push({
            type: 'vault-fusion',
            status: 'source-only',
            source: vaultItem,
            target: null,
            matchKey: matchKey
          });
        }
      });

      fusionProducts.forEach(fusionItem => {
        if (!matchedFusionIds.has(fusionItem.id)) {
          merged.push({
            type: 'vault-fusion',
            status: 'target-only',
            source: null,
            target: fusionItem,
            matchKey: fusionItem.code || fusionItem.name
          });
        }
      });
    }

    // For Vault to BOM Sync
    if (vaultItems.length > 0 && bomItems.length > 0) {
      const matchedBomIds = new Set();

      vaultItems.forEach(vaultItem => {
        const matchKey = vaultItem.number || vaultItem.name;
        const matchedBom = bomItems.find(b =>
          b.itemNumber === matchKey ||
          b.number === matchKey
        );

        if (matchedBom) {
          matchedBomIds.add(matchedBom.id || matchedBom.itemNumber);
          merged.push({
            type: 'vault-bom',
            status: 'matched',
            source: vaultItem,
            target: matchedBom,
            matchKey: matchKey
          });
        } else {
          merged.push({
            type: 'vault-bom',
            status: 'source-only',
            source: vaultItem,
            target: null,
            matchKey: matchKey
          });
        }
      });

      bomItems.forEach(bomItem => {
        const bomKey = bomItem.id || bomItem.itemNumber;
        if (!matchedBomIds.has(bomKey)) {
          merged.push({
            type: 'vault-bom',
            status: 'target-only',
            source: null,
            target: bomItem,
            matchKey: bomItem.itemNumber || bomItem.number
          });
        }
      });
    }

    // For QuickBooks to Autodesk Customer/Company Sync
    if (qboCustomers.length > 0 && autodeskCompanies.length > 0) {
      const matchedAutodeskIds = new Set();

      // Match QuickBooks customers with Autodesk companies by DisplayName
      qboCustomers.forEach(qboCustomer => {
        const matchKey = qboCustomer.DisplayName || qboCustomer.CompanyName;
        const matchedAutodesk = autodeskCompanies.find(a =>
          a.name === matchKey
        );

        if (matchedAutodesk) {
          matchedAutodeskIds.add(matchedAutodesk.id);
          merged.push({
            type: 'qbo-autodesk',
            status: 'matched',
            source: qboCustomer,
            target: matchedAutodesk,
            matchKey: matchKey
          });
        } else {
          merged.push({
            type: 'qbo-autodesk',
            status: 'source-only',
            source: qboCustomer,
            target: null,
            matchKey: matchKey
          });
        }
      });

      // Find Autodesk companies not matched to any QuickBooks customer
      autodeskCompanies.forEach(autodeskCompany => {
        if (!matchedAutodeskIds.has(autodeskCompany.id)) {
          merged.push({
            type: 'qbo-autodesk',
            status: 'target-only',
            source: null,
            target: autodeskCompany,
            matchKey: autodeskCompany.name
          });
        }
      });
    }

    // For QuickBooks to Autodesk Vendor/Company Sync
    if (qboVendors.length > 0 && autodeskCompanies.length > 0) {
      console.log('🔍 MERGING VENDOR DATA:');
      console.log('  - QuickBooks vendors count:', qboVendors.length);
      console.log('  - Autodesk companies count:', autodeskCompanies.length);
      console.log('  - First vendor:', qboVendors[0]);
      console.log('  - First company:', autodeskCompanies[0]);

      const matchedAutodeskIds = new Set();

      // Match QuickBooks vendors with Autodesk companies by DisplayName
      qboVendors.forEach(qboVendor => {
        const matchKey = qboVendor.DisplayName || qboVendor.CompanyName;
        console.log('  - Looking for match for vendor:', matchKey);
        const matchedAutodesk = autodeskCompanies.find(a =>
          a.name === matchKey
        );

        if (matchedAutodesk) {
          console.log('  - ✅ MATCH FOUND:', matchKey, '->', matchedAutodesk.name);
          matchedAutodeskIds.add(matchedAutodesk.id);
          merged.push({
            type: 'qbo-vendor-autodesk',
            status: 'matched',
            source: qboVendor,
            target: matchedAutodesk,
            matchKey: matchKey
          });
        } else {
          console.log('  - ❌ NO MATCH for vendor:', matchKey);
          merged.push({
            type: 'qbo-vendor-autodesk',
            status: 'source-only',
            source: qboVendor,
            target: null,
            matchKey: matchKey
          });
        }
      });

      // Find Autodesk companies not matched to any QuickBooks vendor
      autodeskCompanies.forEach(autodeskCompany => {
        if (!matchedAutodeskIds.has(autodeskCompany.id)) {
          merged.push({
            type: 'qbo-vendor-autodesk',
            status: 'target-only',
            source: null,
            target: autodeskCompany,
            matchKey: autodeskCompany.name
          });
        }
      });
    }

    // For Autodesk Project to QuickBooks Project Sync
    const autodeskProjects = autodeskProjectsData?.results || [];
    const qboProjects = qboData?.Customer || []; // QuickBooks projects are customers with IsProject=true

    console.log('🔍 PROJECT SYNC DATA CHECK:');
    console.log('  - Autodesk projects count:', autodeskProjects.length);
    console.log('  - QuickBooks projects count:', qboProjects.length);
    console.log('  - autodeskProjectsData:', autodeskProjectsData);
    console.log('  - qboData:', qboData);

    if (autodeskProjects.length > 0 || qboProjects.length > 0) {
      console.log('🔍 MERGING PROJECT DATA:');
      console.log('  - Autodesk projects count:', autodeskProjects.length);
      console.log('  - QuickBooks projects count:', qboProjects.length);
      console.log('  - First Autodesk project:', autodeskProjects[0]);
      console.log('  - First QuickBooks project:', qboProjects[0]);

      const matchedProjectIds = new Set();

      // Match Autodesk projects with QuickBooks projects by name
      autodeskProjects.forEach(project => {
        const matchKey = project.name;
        console.log('  - Looking for match for Autodesk project:', matchKey);
        const matchedProject = qboProjects.find(p =>
          p.DisplayName === matchKey || p.CompanyName === matchKey
        );

        if (matchedProject) {
          console.log('  - ✅ MATCH FOUND:', matchKey, '->', matchedProject.DisplayName);
          matchedProjectIds.add(matchedProject.Id);
          merged.push({
            type: 'autodesk-project-qbo-project',
            status: 'matched',
            source: project,
            target: matchedProject,
            matchKey: matchKey
          });
        } else {
          console.log('  - ❌ NO MATCH for Autodesk project:', matchKey);
          merged.push({
            type: 'autodesk-project-qbo-project',
            status: 'source-only',
            source: project,
            target: null,
            matchKey: matchKey
          });
        }
      });

      // Find QuickBooks projects not matched to any Autodesk project
      qboProjects.forEach(qboProject => {
        if (!matchedProjectIds.has(qboProject.Id)) {
          merged.push({
            type: 'autodesk-project-qbo-project',
            status: 'target-only',
            source: null,
            target: qboProject,
            matchKey: qboProject.DisplayName || qboProject.CompanyName
          });
        }
      });
    }

    // If only source data exists
    if (vaultItems.length > 0 && unanetProducts.length === 0 && fusionProducts.length === 0) {
      vaultItems.forEach(item => {
        merged.push({
          type: 'vault-only',
          status: 'source-only',
          source: item,
          target: null,
          matchKey: item.number || item.name
        });
      });
    }

    // For Autodesk Docs files (file copy operation) - use sync records
    // Check if this is a copy files flow and use syncRecords for display
    const isCopyFilesFlow = config?.flowName?.toLowerCase().includes('copy') || config?.flowName?.toLowerCase().includes('file');

    if (isCopyFilesFlow && syncRecords.length > 0) {
      syncRecords.forEach(record => {
        // Extract information from sync record
        const sourceId = record.sourceId || 'Unknown';
        const targetId = record.targetId || null;
        const status = record.status || 'Unknown';
        const message = record.message || '';

        // Determine status: skipped files are treated as 'source-only' (Not Synced)
        // EXCEPT those skipped due to "already integrated" (those stay as 'matched' for display)
        let mappedStatus;
        if (status === 'Integrated' || status?.toLowerCase() === 'success') {
          mappedStatus = 'matched';
        } else if (status === 'started') {
          mappedStatus = 'source-only';
        } else if (status === 'failed') {
          mappedStatus = 'source-only';  // Failed files show in "Not Synced"
        } else if (status?.toLowerCase() === 'skipped') {
          // Check if skipped because file was already successfully copied/integrated
          const isAlreadyCopied = message?.toLowerCase().includes('already integrated') ||
            message?.toLowerCase().includes('skipped on re-run') ||
            message?.toLowerCase().includes('already copied to this destination') ||
            message?.toLowerCase().includes('already copied to destination');
          if (isAlreadyCopied) {
            // Already copied/integrated - show as matched (green) since it was successfully synced before
            mappedStatus = 'matched';
          } else {
            // Skipped for other reasons (not whitelisted, C4R model, etc.) - show in "Not Synced" (orange)
            mappedStatus = 'source-only';
          }
        } else {
          mappedStatus = status?.toLowerCase() || 'unknown';
        }

        merged.push({
          type: 'autodesk-docs-files',
          status: mappedStatus,
          logId: record.logId, // Include logId for verification
          source: {
            id: sourceId,
            name: record.sourceDesc || sourceId,
            description: record.sourceDesc,
            data: record.sourceData,
            fileSize: record.sourceFileSize,
            projectId: record.sourceProjectId
          },
          target: targetId ? {
            id: targetId,
            name: record.targetDesc || targetId,
            description: record.targetDesc,
            fileSize: record.targetFileSize,
            projectId: record.targetProjectId
          } : null,
          matchKey: sourceId,
          fileName: record.sourceDesc || sourceId,
          fileType: 'Autodesk Docs File',
          syncMessage: cleanupMessage(message, status),  // ✨ CLEANED UP: Use helper function
          syncDateTime: record.syncDateTime,
          originalStatus: status,  // Keep original status for reference
          isSkipped: status?.toLowerCase() === 'skipped',
          isFailed: status?.toLowerCase() === 'failed',
          srcProjectId: record.sourceProjectId,
          dstProjectId: record.targetProjectId
        });
      });
    }

    // For Autodesk Vault To Fusion Operation Products - use sync records
    const isVaultToFusionOperationFlow = config?.flowName === "Autodesk Vault To Fusion Operation Products";

    if (isVaultToFusionOperationFlow && syncRecords.length > 0) {
      syncRecords.forEach(record => {
        // Find Fusion product name by targetId (product code)
        const fusionProduct = fusionProducts.find(p => p.code === record.targetId);
        
        merged.push({
          type: 'vault-fusion-operation',
          status: (record.status === 'Integrated' || record.status === 'Integrated_Update') ? 'matched' : 'source-only',
          sourceId: record.sourceId || 'Unknown',
          sourceData: record.sourceData,
          targetId: record.targetId,
          targetName: fusionProduct ? fusionProduct.name : (record.targetDesc && record.targetDesc !== "Fusion Operation Product" ? record.targetDesc : '-'),
          syncMessage: record.message,
          syncDateTime: record.syncDateTime
        });
      });
    }

    return merged;
  }, [vaultData, fusionData, unanetData, bomData, qboData, autodeskData, autodeskProjectsData, autodeskDocsData, syncRecords, config]);

  // Filter and search functionality
  const filteredData = useMemo(() => {
    let filtered = mergedData;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const sourceSearch = item.source ?
          `${item.source.number || ''} ${item.source.name || ''} ${item.source.title || ''}`.toLowerCase() : '';
        const targetSearch = item.target ?
          `${item.target.product || ''} ${item.target.barcode || ''} ${item.target.description || ''}`.toLowerCase() : '';
        return sourceSearch.includes(searchLower) || targetSearch.includes(searchLower);
      });
    }

    return filtered;
  }, [mergedData, statusFilter, searchTerm]);

  // Pagination - Reset to page 1 when filters change
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, rowsPerPage]);

  // Helper function to resolve project name from project ID
  const getProjectNameById = (projectId) => {
    if (!projectId) return projectId;

    // Helper to extract project name from project object
    const extractProjectName = (project) => {
      if (!project) return null;

      // Try multiple possible paths for project name
      const name =
        project.attributes?.name?.name ||
        project.attributes?.name ||
        project.attributes?.displayName ||
        project.displayName ||
        project.name ||
        null;

      // Only return name if it's different from the ID (not a fallback)
      return name && name !== projectId ? name : null;
    };

    // Try to find in projects state
    const project = projects.find(p => p.id === projectId);
    if (project) {
      const name = extractProjectName(project);
      if (name) return name;
    }

    // Fallback: try to get from copyMappings
    const mapping = copyMappings.find(m =>
      m.dstProjectId === projectId || m.srcProjectId === projectId
    );
    if (mapping) {
      if (projectId === mapping.dstProjectId) {
        const name = mapping.dstProjectName;
        // Only return name if it's different from ID
        if (name && name !== projectId && name.trim() !== '') return name;
      } else if (projectId === mapping.srcProjectId) {
        const name = mapping.srcProjectName;
        // Only return name if it's different from ID
        if (name && name !== projectId && name.trim() !== '') return name;
      }
    }

    // If we couldn't find a proper name, return formatted ID
    const shortId = projectId.length > 12 ? `${projectId.substring(0, 12)}...` : projectId;
    return `[ID: ${shortId}]`;
  };

  // Build mapping pairs list from copyMappings or derive from file data
  const mappingPairs = useMemo(() => {
    // Helper to format project name for display
    const formatProjectNameForLabel = (projectName, projectId) => {
      // If name is missing, empty, or same as ID, show formatted ID
      if (!projectName || projectName.trim() === '' || projectName === projectId) {
        const shortId = projectId && projectId.length > 12 ? `${projectId.substring(0, 12)}...` : projectId;
        return projectId ? `[ID: ${shortId}]` : '[Unknown]';
      }
      return projectName;
    };

    // First, try to use saved mappings (which have project names)
    if (copyMappings && copyMappings.length > 0) {
      return copyMappings.map((mapping, idx) => {
        const srcName = formatProjectNameForLabel(mapping.srcProjectName, mapping.srcProjectId);
        const dstName = formatProjectNameForLabel(mapping.dstProjectName, mapping.dstProjectId);
        return {
          key: `${mapping.srcProjectId}|${mapping.dstProjectId}`,
          label: `${srcName} > ${dstName}`,
          srcProjectId: mapping.srcProjectId,
          dstProjectId: mapping.dstProjectId
        };
      });
    }

    // If no saved mappings, derive from actual file data
    // This ensures filter works even if mappings weren't loaded from parameters
    const copyFilesItems = mergedData?.filter(item => item.type === 'autodesk-docs-files') || [];
    if (copyFilesItems.length === 0) return [];

    // Extract unique project pairs from files
    const uniquePairs = new Map();
    copyFilesItems.forEach(item => {
      if (item.srcProjectId && item.dstProjectId) {
        const key = `${item.srcProjectId}|${item.dstProjectId}`;
        if (!uniquePairs.has(key)) {
          // Resolve project names from projects state or copyMappings
          const srcProjectName = getProjectNameById(item.srcProjectId);
          const dstProjectName = getProjectNameById(item.dstProjectId);

          uniquePairs.set(key, {
            key,
            label: `${srcProjectName} > ${dstProjectName}`,
            srcProjectId: item.srcProjectId,
            dstProjectId: item.dstProjectId
          });
        }
      }
    });

    return Array.from(uniquePairs.values());
  }, [copyMappings, mergedData, projects]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  // Reset to page 1 when search term or status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Action handlers

  const handleRefresh = () => {
    setLoading(true);
    fetchDataPointConfig();
  };

  const handleColumnToggle = (column) => {
    setColumnVisibility(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={handleBack}
        >
          Back to Sync Flow
        </Button>
      </Box>
    );
  }

  const getRowStyle = (status) => {
    switch (status) {
      case 'matched':
        return { backgroundColor: '#e8f5e9' }; // Light green
      case 'source-only':
        return { backgroundColor: '#fff3e0' }; // Light orange
      case 'target-only':
        return { backgroundColor: '#e3f2fd' }; // Light blue
      default:
        return {};
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'matched':
        return <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />;
      case 'source-only':
        return <Warning sx={{ color: '#ff9800', fontSize: 20 }} />;
      case 'target-only':
        return <Info sx={{ color: '#2196f3', fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'matched':
        return 'Synced';
      case 'source-only':
        return 'Not in Target';
      case 'target-only':
        return 'Target Only';
      default:
        return 'Unknown';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  // Verification functions for Autodesk Copy Files
  const formatVerificationStatus = (status) => {
    if (!status) return '';
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'mismatch':
        return 'Mismatch';
      case 'source_missing':
        return 'Source Missing';
      case 'target_missing':
        return 'Target Missing';
      case 'error':
        return 'Error';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getVerificationChipStyles = (status) => {
    switch (status) {
      case 'verified':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'mismatch':
        return { backgroundColor: '#fff3e0', color: '#f57c00' };
      case 'source_missing':
      case 'target_missing':
        return { backgroundColor: '#ffebee', color: '#c62828' };
      default:
        return { backgroundColor: '#eceff1', color: '#455a64' };
    }
  };

  const renderVerificationTooltipContent = (result) => {
    if (!result) {
      return <Typography variant="body2">Verify whether the file exists in both source and destination Autodesk Docs projects.</Typography>;
    }

    const checks = Array.isArray(result.checks) ? result.checks : [];

    return (
      <Box sx={{ maxWidth: 320 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {result.message || 'Verification completed'}
        </Typography>
        {checks.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {checks.map((check, idx) => (
              <Typography
                key={idx}
                variant="caption"
                sx={{
                  display: 'block',
                  color: check.status === 'failed' ? 'error.main' : 'success.main',
                  mb: 0.3
                }}
              >
                {check.status === 'failed' ? '✖' : '✔'} {check.detail}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const verifySyncRecord = async (record) => {
    if (!record?.logId) {
      toast.warn('Verification is unavailable for this record (missing log reference).');
      return;
    }

    setVerifyStatus((prev) => ({
      ...prev,
      [record.logId]: {
        ...prev[record.logId],
        loading: true
      }
    }));

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/datapoint/autodesk-copy-files/verify`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          logId: record.logId,
          flowId: config?.masterFlowId || masterFlowId,
          tenantId
        }),
      });

      const data = await response.json();

      setVerifyStatus((prev) => ({
        ...prev,
        [record.logId]: {
          loading: false,
          data
        }
      }));

      if (response.ok) {
        toast.success(data.message || 'Verification completed successfully');
      } else {
        toast.error(data.message || 'Verification failed');
      }
    } catch (err) {
      console.error('Error verifying sync record:', err);
      setVerifyStatus((prev) => ({
        ...prev,
        [record.logId]: {
          loading: false,
          error: err.message,
          data: prev[record.logId]?.data
        }
      }));
      toast.error(`Verification failed: ${err.message}`);
    }
  };

  const renderEnhancedFilters = () => (
    <Collapse in={showFilters}>
      <Card sx={{ mb: 2, backgroundColor: '#f8f9fa' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <Close />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Items</MenuItem>
                  <MenuItem value="matched">Synced Only</MenuItem>
                  <MenuItem value="source-only">Not Synced</MenuItem>
                  <MenuItem value="target-only">Target Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Rows per page</InputLabel>
                <Select
                  value={rowsPerPage}
                  label="Rows per page"
                  onChange={(e) => setRowsPerPage(e.target.value)}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Collapse>
  );

  const renderVaultToUnanetComparison = (items) => {
    // Use filteredData for consistent counts
    const matchedCount = filteredData.filter(i => i.status === 'matched').length;
    const sourceOnlyCount = filteredData.filter(i => i.status === 'source-only').length;
    const targetOnlyCount = filteredData.filter(i => i.status === 'target-only').length;
    const totalCount = filteredData.length;

    return (
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: '#f8f9fa',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <SyncAlt sx={{ color: '#1976d2' }} />
            <Typography variant="h6">
              Vault ↔ Unanet Product Sync ({totalCount} items)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {/* Enhanced Filters */}
          {renderEnhancedFilters()}

          {/* Simplified Table Controls */}
          <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedData.length} of {filteredData.length} items
                {searchTerm && ` • Filtered by "${searchTerm}"`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleRefresh} title="Refresh data">
                  <Refresh fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                  title="Toggle filters"
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 80 }}>Sync Status</TableCell>
                  {columnVisibility.sourceNumber && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vault Number</TableCell>
                  )}
                  {columnVisibility.sourceTitle && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vault Title</TableCell>
                  )}
                  {columnVisibility.sourceRevision && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Revision</TableCell>
                  )}
                  {columnVisibility.sourceState && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vault State</TableCell>
                  )}
                  {columnVisibility.targetProduct && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Unanet Product</TableCell>
                  )}
                  {columnVisibility.targetDescription && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Description</TableCell>
                  )}
                  {columnVisibility.targetVersion && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Version</TableCell>
                  )}
                  {columnVisibility.targetStatus && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Active Status</TableCell>
                  )}
                  {columnVisibility.targetCategory && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Category</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      ...getRowStyle(item.status),
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <TableCell>
                      <Tooltip title={getStatusLabel(item.status)}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getStatusIcon(item.status)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    {/* Source: Vault */}
                    {columnVisibility.sourceNumber && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.source?.number || item.source?.name || '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.sourceTitle && (
                      <TableCell>{item.source?.title || '-'}</TableCell>
                    )}
                    {columnVisibility.sourceRevision && (
                      <TableCell>{item.source?.revision || '-'}</TableCell>
                    )}
                    {columnVisibility.sourceState && (
                      <TableCell>
                        {item.source?.state ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: item.source.state === 'Released' ? '#2e7d32' : '#f57c00'
                            }}
                          >
                            {item.source.state}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                    )}
                    {/* Target: Unanet */}
                    {columnVisibility.targetProduct && (
                      <TableCell>
                        {item.target?.product || item.target?.barcode ? (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.target.product || item.target.barcode}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Not synced
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.targetDescription && (
                      <TableCell>
                        {item.target?.description || item.target?.full_description ? (
                          item.target.description || item.target.full_description
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Not synced
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.targetVersion && (
                      <TableCell>
                        {item.target?.version && item.target.version !== '-' ? (
                          item.target.version
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.targetStatus && (
                      <TableCell>
                        {item.target?.active ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: item.target.active === 'Active' ? '#2e7d32' : '#757575'
                            }}
                          >
                            {item.target.active}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.targetCategory && (
                      <TableCell>
                        {item.target?.category_1 && item.target.category_1 !== '-' ? (
                          item.target.category_1
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {totalPages}
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderVaultToFusionComparison = (items) => {
    // Use filteredData for consistent counts
    const matchedCount = filteredData.filter(i => i.status === 'matched').length;
    const sourceOnlyCount = filteredData.filter(i => i.status === 'source-only').length;
    const targetOnlyCount = filteredData.filter(i => i.status === 'target-only').length;
    const totalCount = filteredData.length;

    return (
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: '#f8f9fa',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <SyncAlt sx={{ color: '#1976d2' }} />
            <Typography variant="h6">
              Vault ↔ Fusion Operation Sync ({totalCount} items)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {/* Enhanced Filters */}
          {renderEnhancedFilters()}

          {/* Simplified Table Controls */}
          <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedData.length} of {filteredData.length} items
                {searchTerm && ` • Filtered by "${searchTerm}"`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleRefresh} title="Refresh data">
                  <Refresh fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                  title="Toggle filters"
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 80 }}>Sync Status</TableCell>
                  {columnVisibility.sourceNumber && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vault Number</TableCell>
                  )}
                  {columnVisibility.sourceTitle && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vault Title</TableCell>
                  )}
                  {columnVisibility.sourceRevision && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Revision</TableCell>
                  )}
                  {columnVisibility.sourceState && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vault State</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Fusion Code</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Fusion Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      ...getRowStyle(item.status),
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <TableCell>
                      <Tooltip title={getStatusLabel(item.status)}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getStatusIcon(item.status)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    {/* Source: Vault */}
                    {columnVisibility.sourceNumber && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.source?.number || item.source?.name || '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.sourceTitle && (
                      <TableCell>
                        <Typography variant="body2">
                          {item.source?.title || '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.sourceRevision && (
                      <TableCell>
                        <Typography variant="body2">
                          {item.source?.revision || '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.sourceState && (
                      <TableCell>
                        {item.source?.state ? (
                          <Typography variant="body2">
                            {item.source.state}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                    )}
                    {/* Target: Fusion */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.target?.code || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.target?.name || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {Math.ceil(filteredData.length / rowsPerPage)}
            </Typography>
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderQboToAutodeskComparison = (items) => {
    const totalCount = filteredData.length;

    return (
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: '#f8f9fa',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <SyncAlt sx={{ color: '#1976d2' }} />
            <Typography variant="h6">
              QuickBooks ↔ Autodesk Customer/Company Sync ({totalCount} items)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {/* Enhanced Filters */}
          {renderEnhancedFilters()}

          {/* Simplified Table Controls */}
          <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedData.length} of {filteredData.length} items
                {searchTerm && ` • Filtered by "${searchTerm}"`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleRefresh} title="Refresh data">
                  <Refresh fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                  title="Toggle filters"
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 80 }}>Sync Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>QB Customer ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>QB Display Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Autodesk Company</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Autodesk Trade</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Autodesk ERP ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      ...getRowStyle(item.status),
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <TableCell>
                      <Tooltip title={getStatusLabel(item.status)}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getStatusIcon(item.status)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    {/* Source: QuickBooks Customer */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.source?.Id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.source?.DisplayName || item.source?.CompanyName || '-'}
                      </Typography>
                    </TableCell>
                    {/* Target: Autodesk Company */}
                    <TableCell>
                      {item.target?.name ? (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.target.name}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Not synced
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.target?.trade || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.target?.erp_id || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {Math.ceil(filteredData.length / rowsPerPage)}
            </Typography>
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderQboVendorToAutodeskComparison = (items) => {
    const totalCount = filteredData.length;

    return (
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: '#f8f9fa',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <SyncAlt sx={{ color: '#1976d2' }} />
            <Typography variant="h6">
              QuickBooks ↔ Autodesk Vendor/Company Sync ({totalCount} items)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {/* Enhanced Filters */}
          {renderEnhancedFilters()}

          {/* Simplified Table Controls */}
          <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedData.length} of {filteredData.length} items
                {searchTerm && ` • Filtered by "${searchTerm}"`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleRefresh} title="Refresh data">
                  <Refresh fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                  title="Toggle filters"
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 80 }}>Sync Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>QB Vendor ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>QB Display Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Autodesk Company</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Autodesk Trade</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Autodesk ERP ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      ...getRowStyle(item.status),
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <TableCell>
                      <Tooltip title={getStatusLabel(item.status)}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getStatusIcon(item.status)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    {/* Source: QuickBooks Vendor */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.source?.Id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.source?.DisplayName || item.source?.CompanyName || '-'}
                      </Typography>
                    </TableCell>
                    {/* Target: Autodesk Company */}
                    <TableCell>
                      {item.target?.name ? (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.target.name}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Not synced
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.target?.trade || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.target?.erp_id || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {Math.ceil(filteredData.length / rowsPerPage)}
            </Typography>
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderVaultToBomComparison = (items) => {
    // Use filteredData for consistent counts
    const matchedCount = filteredData.filter(i => i.status === 'matched').length;
    const sourceOnlyCount = filteredData.filter(i => i.status === 'source-only').length;
    const targetOnlyCount = filteredData.filter(i => i.status === 'target-only').length;
    const totalCount = filteredData.length;

    return (
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: '#f8f9fa',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <SyncAlt sx={{ color: '#1976d2' }} />
            <Typography variant="h6">
              Vault ↔ BOM Sync ({totalCount} items)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {/* Enhanced Filters */}
          {renderEnhancedFilters()}

          {/* Simplified Table Controls */}
          <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedData.length} of {filteredData.length} items
                {searchTerm && ` • Filtered by "${searchTerm}"`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleRefresh} title="Refresh data">
                  <Refresh fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                  title="Toggle filters"
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 80 }}>Sync Status</TableCell>
                  {columnVisibility.sourceNumber && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vault Number</TableCell>
                  )}
                  {columnVisibility.sourceTitle && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vault Title</TableCell>
                  )}
                  {columnVisibility.sourceRevision && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Revision</TableCell>
                  )}
                  {columnVisibility.sourceState && (
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vault State</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>BOM Item Number</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>BOM Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Quantity</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Level</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      ...getRowStyle(item.status),
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <TableCell>
                      <Tooltip title={getStatusLabel(item.status)}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getStatusIcon(item.status)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    {/* Source: Vault */}
                    {columnVisibility.sourceNumber && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.source?.number || item.source?.name || '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.sourceTitle && (
                      <TableCell>
                        <Typography variant="body2">
                          {item.source?.title || '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.sourceRevision && (
                      <TableCell>
                        <Typography variant="body2">
                          {item.source?.revision || '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.sourceState && (
                      <TableCell>
                        {item.source?.state ? (
                          <Typography variant="body2">
                            {item.source.state}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                    )}
                    {/* Target: BOM */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.target?.itemNumber || item.target?.number || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.target?.itemName || item.target?.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.target?.quantity || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.target?.level || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {Math.ceil(filteredData.length / rowsPerPage)}
            </Typography>
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderAutodeskProjectToQboProjectComparison = (items) => {
    const totalCount = filteredData.length;

    return (
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: '#f8f9fa',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <SyncAlt sx={{ color: '#1976d2' }} />
            <Typography variant="h6">
              Autodesk Projects ↔ QuickBooks Projects ({totalCount} items)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {/* Enhanced Filters */}
          {renderEnhancedFilters()}

          {/* Simplified Table Controls */}
          <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedData.length} of {filteredData.length} items
                {searchTerm && ` • Filtered by "${searchTerm}"`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleRefresh} title="Refresh data">
                  <Refresh fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                  title="Toggle filters"
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 80 }}>Sync Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>AD Project ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>AD Project Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>AD Project Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>QB Project ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>QB Project Name</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      ...getRowStyle(item.status),
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <TableCell>
                      <Tooltip title={getStatusLabel(item.status)}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getStatusIcon(item.status)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    {/* Source: Autodesk Project */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.source?.id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.source?.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.source?.status || '-'}
                      </Typography>
                    </TableCell>
                    {/* Target: QuickBooks Project */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.target?.Id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.target?.DisplayName || item.target?.CompanyName || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {Math.ceil(filteredData.length / rowsPerPage)}
            </Typography>
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderVaultToFusionOperationComparison = (items) => {
    const totalCount = filteredData.length;

    return (
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: '#f8f9fa',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            <SyncAlt sx={{ color: '#1976d2' }} />
            <Typography variant="h6">
              Vault ↔ Fusion Operation Sync ({totalCount} items)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {/* Enhanced Filters */}
          {renderEnhancedFilters()}

          {/* Simplified Table Controls */}
          <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {paginatedData.length} of {filteredData.length} items
                {searchTerm && ` • Filtered by "${searchTerm}"`}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleRefresh} title="Refresh data">
                  <Refresh fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                  title="Toggle filters"
                >
                  <FilterList fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', width: 80 }}>Sync Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vault Item ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Vault Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Fusion Product ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Fusion Product Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      ...getRowStyle(item.status),
                      '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  >
                    <TableCell>
                      <Tooltip title={getStatusLabel(item.status)}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getStatusIcon(item.status)}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.sourceId || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.sourceData || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.targetId || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.targetName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {item.syncMessage || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {currentPage} of {Math.ceil(filteredData.length / rowsPerPage)}
            </Typography>
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={currentPage}
              onChange={(event, page) => setCurrentPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderAutodeskDocsFiles = (items) => {
    // Helper function to get project name from project ID
    const getProjectName = (projectId) => {
      if (!projectId) return null;
      const project = projects.find(p => p.id === projectId);
      if (project) {
        return project.attributes?.name?.name || project.attributes?.name || project.id;
      }
      // Fallback: try to get from copyMappings if projects not loaded
      const mapping = copyMappings.find(m => m.dstProjectId === projectId || m.srcProjectId === projectId);
      if (mapping && projectId === mapping.dstProjectId) {
        return mapping.dstProjectName || projectId;
      }
      return projectId;
    };

    // Filter items based on selected mapping pair
    const filteredItems = selectedMappingFilter === 'all'
      ? items
      : (() => {
        const [srcProjId, dstProjId] = selectedMappingFilter.split('|');
        return items.filter(item =>
          item.srcProjectId === srcProjId && item.dstProjectId === dstProjId
        );
      })();

    // Apply search filter
    const searchFilteredItems = (() => {
      if (!copyFilesSearchTerm || copyFilesSearchTerm.trim() === '') {
        return filteredItems;
      }
      const searchLower = copyFilesSearchTerm.toLowerCase().trim();
      return filteredItems.filter(item => {
        // Search in source path/name
        const sourcePath = item.source?.description || item.fileName || item.source?.data || '';
        // Search in target path/name
        const targetPath = item.target?.description || item.target?.data || '';
        // Search in file name
        const fileName = item.fileName || '';
        // Search in status/message
        const status = item.status || '';
        const message = item.syncMessage || item.message || '';

        return (
          sourcePath.toLowerCase().includes(searchLower) ||
          targetPath.toLowerCase().includes(searchLower) ||
          fileName.toLowerCase().includes(searchLower) ||
          status.toLowerCase().includes(searchLower) ||
          message.toLowerCase().includes(searchLower)
        );
      });
    })();

    // Apply status filter (all / synced / not-synced)
    const statusFilteredItems = (() => {
      if (copyFilesStatusFilter === 'synced') {
        // Show only successfully synced files (matched status, not failed)
        return searchFilteredItems.filter(item => item.status === 'matched' && !item.isFailed);
      }
      if (copyFilesStatusFilter === 'not-synced') {
        // Show files that are not synced:
        // - source-only status (not copied yet)
        // - failed status
        // - skipped status BUT only if they're not already synced (status !== 'matched')
        //   (skipped files that were already successfully copied have status='matched' and should not appear here)
        return searchFilteredItems.filter(item =>
          item.status === 'source-only' ||
          item.isFailed ||
          (item.isSkipped && item.status !== 'matched')
        );
      }
      return searchFilteredItems;
    })();

    // Apply pagination for Autodesk Docs files (100 records per page)
    const totalPages = Math.max(1, Math.ceil(statusFilteredItems.length / COPY_FILES_ROWS_PER_PAGE));
    const safePage = Math.min(copyFilesPage, totalPages);
    const startIndex = (safePage - 1) * COPY_FILES_ROWS_PER_PAGE;
    const endIndex = startIndex + COPY_FILES_ROWS_PER_PAGE;
    const paginatedItems = statusFilteredItems.slice(startIndex, endIndex);

    return (
      <Card sx={{ mb: 2 }}>
        {/* Header with Title and Filter Buttons */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Description sx={{ color: '#1976d2' }} />
          <Typography variant="h6" sx={{ flex: 1 }}>
            Autodesk Docs Files ({statusFilteredItems.length} files)
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Show:
            </Typography>
            <ToggleButtonGroup
              value={copyFilesStatusFilter}
              exclusive
              size="small"
              onChange={(event, value) => {
                if (!value) return; // ignore deselect
                setCopyFilesStatusFilter(value);
                setCopyFilesPage(1); // reset page on filter change
              }}
              aria-label="Sync status filter"
            >
              <ToggleButton value="all" aria-label="All files">
                All
              </ToggleButton>
              <ToggleButton value="synced" aria-label="Synced files">
                Synced
              </ToggleButton>
              <ToggleButton value="not-synced" aria-label="Not synced files">
                Not Synced
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 0 }}>
          {/* Search Bar and Filters */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap'
            }}
          >
            {/* Search Bar */}
            <TextField
              size="small"
              placeholder="Search files by path, name, or status..."
              value={copyFilesSearchTerm}
              onChange={(e) => {
                setCopyFilesSearchTerm(e.target.value);
                setCopyFilesPage(1); // Reset to first page when search changes
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: copyFilesSearchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setCopyFilesSearchTerm('');
                        setCopyFilesPage(1);
                      }}
                    >
                      <Close />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ flex: 1, minWidth: 300 }}
            />

            {/* Mapping Pair Filter - Hidden when viewing a specific mapping (not 'all') */}
            {mappingPairs.length > 0 && selectedMappingFilter === 'all' && (
              <FormControl size="small" sx={{ minWidth: 300 }}>
                <InputLabel>Filter by Mapping Pair</InputLabel>
                <Select
                  value={selectedMappingFilter}
                  label="Filter by Mapping Pair"
                  onChange={(e) => {
                    setSelectedMappingFilter(e.target.value);
                    setCopyFilesPage(1); // Reset to first page when filter changes
                  }}
                >
                  <MenuItem value="all">All Mappings ({items.length} files)</MenuItem>
                  {mappingPairs.map((pair) => {
                    const count = items.filter(item =>
                      item.srcProjectId === pair.srcProjectId && item.dstProjectId === pair.dstProjectId
                    ).length;
                    return (
                      <MenuItem key={pair.key} value={pair.key}>
                        {pair.label} ({count} files)
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          </Box>

          {/* Table */}
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="medium">
              <TableHead>
                <TableRow>
                  <TableCell sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    py: 2,
                    borderBottom: 2,
                    borderColor: '#e0e0e0'
                  }}>Source Path</TableCell>
                  <TableCell sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    py: 2,
                    borderBottom: 2,
                    borderColor: '#e0e0e0',
                    width: '100px'
                  }}>Source Size</TableCell>
                  <TableCell sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    py: 2,
                    borderBottom: 2,
                    borderColor: '#e0e0e0'
                  }}>Target</TableCell>
                  <TableCell sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    py: 2,
                    borderBottom: 2,
                    borderColor: '#e0e0e0',
                    width: '100px'
                  }}>Target Size</TableCell>
                  <TableCell sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    py: 2,
                    borderBottom: 2,
                    borderColor: '#e0e0e0',
                    width: '120px'
                  }}>Status</TableCell>
                  <TableCell sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    py: 2,
                    borderBottom: 2,
                    borderColor: '#e0e0e0',
                    width: '140px'
                  }}>Date</TableCell>
                  <TableCell sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    py: 2,
                    borderBottom: 2,
                    borderColor: '#e0e0e0',
                    width: '80px',
                    textAlign: 'center'
                  }}>Info</TableCell>
                  <TableCell sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    py: 2,
                    borderBottom: 2,
                    borderColor: '#e0e0e0',
                    textAlign: 'right',
                    width: '180px'
                  }}>Verify</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedItems.map((item, index) => {
                  // Debug: Log the item structure to understand what data we have
                  if (index === 0) {
                    console.log('🔍 DEBUG: First Autodesk Docs item structure:', {
                      item,
                      sourceData: item.source?.data,
                      sourceDesc: item.source?.description,
                      targetDesc: item.target?.description,
                      fileName: item.fileName,
                      sourceId: item.source?.id,
                      targetId: item.target?.id
                    });
                  }

                  // Extract meaningful paths from available data
                  let sourcePath = 'Unknown';
                  let targetPath = 'N/A';

                  // Try to get source path from various sources
                  if (item.source?.data && item.source.data.includes(' - ')) {
                    // sourceData format: "fileName - /full/path/to/file"
                    const parts = item.source.data.split(' - ');
                    if (parts.length >= 2) {
                      sourcePath = parts.slice(1).join(' - ').trim();
                    } else {
                      sourcePath = item.source.data;
                    }
                  } else if (item.fileName) {
                    sourcePath = item.fileName;
                  } else if (item.source?.description && item.source.description !== 'Autodesk Docs File') {
                    sourcePath = item.source.description;
                  } else if (item.source?.id) {
                    // Use source ID as fallback
                    sourcePath = `Source: ${item.source.id}`;
                  }

                  // Construct target path - show destination location with project name
                  if (item.target?.id) {
                    // Extract filename from source path if available
                    let fileName = 'File';
                    if (sourcePath && sourcePath !== 'Unknown') {
                      // Extract filename from the end of the path
                      const pathParts = sourcePath.split('/');
                      fileName = pathParts[pathParts.length - 1] || 'File';
                    } else if (item.fileName) {
                      fileName = item.fileName;
                    }

                    // Get target project name
                    const targetProjectId = item.dstProjectId || item.target?.projectId;
                    const targetProjectName = targetProjectId ? getProjectName(targetProjectId) : null;

                    // Show project name > file name format
                    if (targetProjectName && targetProjectName !== targetProjectId) {
                      targetPath = `${targetProjectName} > ${fileName}`;
                    } else {
                      // Fallback if project name not found
                      targetPath = fileName;
                    }
                  } else if (item.fileName) {
                    // Check if sync failed - don't show "(copied to destination)" for failed syncs
                    const isFailed = item.isFailed || item.originalStatus?.toLowerCase() === 'failed';

                    if (isFailed) {
                      // For failed syncs, show "N/A" or "Failed" instead of "(copied to destination)"
                      targetPath = 'N/A';
                    } else {
                      // Get target project name even if target.id is missing (for skipped files that were already copied)
                      const targetProjectId = item.dstProjectId || item.target?.projectId;
                      const targetProjectName = targetProjectId ? getProjectName(targetProjectId) : null;

                      if (targetProjectName && targetProjectName !== targetProjectId) {
                        targetPath = `${targetProjectName} > ${item.fileName} (copied to destination)`;
                      } else {
                        targetPath = `${item.fileName} (copied to destination)`;
                      }
                    }
                  } else {
                    targetPath = 'N/A';
                  }

                  return (
                    <TableRow key={index} hover sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                      <TableCell sx={{ py: 2 }}>
                        <Tooltip title={sourcePath} arrow placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.8125rem',
                              color: '#424242',
                              cursor: 'help'
                            }}
                          >
                            {truncatePath(sourcePath, 60)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: '#616161' }}>
                          {formatFileSize(item.source?.fileSize)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Tooltip title={targetPath} arrow placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.8125rem',
                              color: '#424242',
                              cursor: 'help'
                            }}
                          >
                            {truncatePath(targetPath, 60)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: '#616161' }}>
                          {formatFileSize(item.target?.fileSize)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {(() => {
                          // Determine label and color based on status
                          let label = 'Unknown';
                          let backgroundColor = '#fff3e0';
                          let color = '#e65100';

                          if (item.status === 'matched') {
                            label = 'Synced';
                            backgroundColor = '#e8f5e9';
                            color = '#2e7d32';
                          } else if (item.isFailed) {
                            label = 'Failed';
                            backgroundColor = '#ffebee';
                            color = '#c62828';
                          } else if (item.isSkipped) {
                            label = 'Skipped';
                            backgroundColor = '#fff3e0';
                            color = '#f57c00';
                          } else if (item.status === 'source-only') {
                            label = 'Not Synced';
                            backgroundColor = '#fff3e0';
                            color = '#e65100';
                          } else {
                            label = item.originalStatus || item.status || 'Unknown';
                          }

                          return (
                            <Chip
                              label={label}
                              size="small"
                              sx={{
                                backgroundColor,
                                color,
                                fontWeight: 500,
                                fontSize: '0.75rem'
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.8125rem',
                            color: '#616161',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.syncDateTime ?
                            formatAppDateTime(item.syncDateTime)
                            :
                            '-'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2, textAlign: 'center' }}>
                        {item.syncMessage && item.syncMessage !== '-' ? (
                          <Tooltip
                            title={
                              <Box sx={{ p: 0.5 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                  {item.syncMessage}
                                </Typography>
                              </Box>
                            }
                            arrow
                            placement="left"
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                setMessagePopoverAnchor(e.currentTarget);
                                setMessagePopoverContent({
                                  message: item.syncMessage,
                                  status: item.originalStatus || item.status
                                });
                              }}
                              sx={{
                                color: item.isFailed ? '#c62828' :
                                  item.syncMessage?.toLowerCase().includes('already') ? '#1976d2' :
                                    item.syncMessage?.toLowerCase().includes('success') ? '#2e7d32' :
                                      '#757575',
                                '&:hover': {
                                  backgroundColor: item.isFailed ? '#ffebee' :
                                    item.syncMessage?.toLowerCase().includes('already') ? '#e3f2fd' :
                                      item.syncMessage?.toLowerCase().includes('success') ? '#e8f5e9' :
                                        '#f5f5f5'
                                }
                              }}
                            >
                              {item.isFailed ? (
                                <ErrorIcon fontSize="small" />
                              ) : item.syncMessage?.toLowerCase().includes('already') ? (
                                <CheckCircle fontSize="small" />
                              ) : item.syncMessage?.toLowerCase().includes('success') ? (
                                <CheckCircle fontSize="small" />
                              ) : (
                                <HelpOutline fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: '#bdbdbd' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2, textAlign: 'right' }}>
                        {(() => {
                          const verifyState = verifyStatus[item.logId];
                          const isVerifying = Boolean(verifyState?.loading);
                          const verifyResult = verifyState?.data;
                          const chipStyles = verifyResult ? getVerificationChipStyles(verifyResult.status) : null;
                          return (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                              {verifyResult?.status && (
                                <Chip
                                  size="small"
                                  label={formatVerificationStatus(verifyResult.status)}
                                  sx={{
                                    fontWeight: 500,
                                    ...chipStyles
                                  }}
                                />
                              )}
                              <Tooltip title={renderVerificationTooltipContent(verifyResult)}>
                                <span>
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => verifySyncRecord(item)}
                                    disabled={isVerifying || !item.logId}
                                  >
                                    {isVerifying ? (
                                      <CircularProgress size={16} />
                                    ) : verifyResult?.status ? 'Re-Verify' : 'Verify'}
                                  </Button>
                                </span>
                              </Tooltip>
                            </Box>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Message Details Popover */}
          <Popover
            open={Boolean(messagePopoverAnchor)}
            anchorEl={messagePopoverAnchor}
            onClose={() => setMessagePopoverAnchor(null)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiPopover-paper': {
                maxWidth: 500,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
              }
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                {messagePopoverContent.status?.toLowerCase() === 'failed' ? (
                  <ErrorIcon sx={{ color: '#c62828', mr: 1 }} />
                ) : messagePopoverContent.message?.toLowerCase().includes('already') ? (
                  <CheckCircle sx={{ color: '#1976d2', mr: 1 }} />
                ) : messagePopoverContent.message?.toLowerCase().includes('success') ? (
                  <CheckCircle sx={{ color: '#2e7d32', mr: 1 }} />
                ) : (
                  <Info sx={{ color: '#757575', mr: 1 }} />
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {messagePopoverContent.status?.toLowerCase() === 'failed'
                    ? 'Error Details'
                    : messagePopoverContent.message?.toLowerCase().includes('already')
                      ? 'Already Synced'
                      : messagePopoverContent.message?.toLowerCase().includes('success')
                        ? 'Sync Success'
                        : 'Sync Information'
                  }
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: '#424242',
                  lineHeight: 1.6,
                  wordBreak: 'break-word'
                }}
              >
                {messagePopoverContent.message}
              </Typography>
            </Box>
          </Popover>

          {/* Pagination for Autodesk Docs files */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Page {copyFilesPage} of {Math.max(1, Math.ceil(statusFilteredItems.length / COPY_FILES_ROWS_PER_PAGE))} &nbsp;
              ({statusFilteredItems.length} file{statusFilteredItems.length === 1 ? '' : 's'})
            </Typography>
            <Pagination
              count={Math.max(1, Math.ceil(statusFilteredItems.length / COPY_FILES_ROWS_PER_PAGE))}
              page={copyFilesPage}
              onChange={(event, page) => setCopyFilesPage(page)}
              color="primary"
              size="small"
            />
          </Box>
        </Box>
      </Card>
    );
  };

  // Recursive Folder Row Component for deep folder expansion (up to 8 levels)
  const FolderRow = ({
    file,
    projectId,
    isSource,
    level = 0,
    expandedFolders,
    setExpandedFolders,
    fetchSubfolderContents
  }) => {
    const folderId = `${file.id}_${level}`;
    const isExpanded = expandedFolders[folderId];
    const subfolders = isExpanded ? (subfolderContents[file.id]?.data || []) : [];

    // Check if this folder is selected for copying (only for source folders)
    const selectedSourceFolderIds = window.selectedSourceFolderIds || [];
    const isSelectedForCopying = isSource && selectedSourceFolderIds.includes(file.id);

    return (
      <>
        <TableRow
          hover
          onClick={(e) => {
            e.stopPropagation();
            const isFolder = file.type === 'folders';
            console.log(`📁 Level ${level} folder clicked:`, { folderId, isFolder });

            if (isFolder) {
              if (isExpanded) {
                console.log(`🔽 Collapsing level ${level}:`, folderId);
                setExpandedFolders(prev => {
                  const newState = { ...prev };
                  delete newState[folderId];
                  return newState;
                });
              } else {
                console.log(`🔼 Expanding level ${level}:`, folderId);
                setExpandedFolders(prev => ({ ...prev, [folderId]: true }));
                if (projectId && file.id) {
                  fetchSubfolderContents(file.id, projectId, isSource);
                }
              }
            }
          }}
          sx={{
            cursor: file.type === 'folders' ? 'pointer' : 'default',
            pl: level * 2,
            backgroundColor: isSelectedForCopying ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
            borderLeft: isSelectedForCopying ? '3px solid #1976d2' : 'none',
            '&:hover': {
              backgroundColor: isSelectedForCopying ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <TableCell sx={{ pl: `${4 + level * 2}px` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {file.type === 'folders' ? (
                isExpanded ? '📂' : '📁'
              ) : '📄'}
              <Typography
                variant="body2"
                color={level > 0 ? 'text.secondary' : 'inherit'}
                sx={{
                  fontWeight: isSelectedForCopying ? 'bold' : 'normal',
                  color: isSelectedForCopying ? '#1976d2' : 'inherit'
                }}
              >
                {file.attributes?.displayName || file.attributes?.name || 'Unknown'}
              </Typography>
              {isSelectedForCopying && (
                <Chip
                  label="Selected"
                  size="small"
                  color="primary"
                  sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Box>
          </TableCell>
          <TableCell>
            <Chip
              label={file.type === 'folders' ? 'Folder' : 'File'}
              size="small"
              color={file.type === 'folders' ? 'primary' : 'default'}
            />
          </TableCell>
          <TableCell>
            {file.attributes?.storageSize
              ? `${(file.attributes.storageSize / 1024).toFixed(2)} KB`
              : file.type === 'folders' ? '-' : '-'}
          </TableCell>
        </TableRow>

        {/* Recursively render subfolders up to 8 levels deep */}
        {isExpanded && file.type === 'folders' && level < 8 && subfolders.length > 0 && subfolders.map((subfolder, idx) => (
          <FolderRow
            key={`subfolder-${folderId}-${idx}`}
            file={subfolder}
            projectId={projectId}
            isSource={isSource}
            level={level + 1}
            expandedFolders={expandedFolders}
            setExpandedFolders={setExpandedFolders}
            fetchSubfolderContents={fetchSubfolderContents}
          />
        ))}
      </>
    );
  };

  const renderFolderContents = () => {
    console.log('🎨 Rendering folder contents...', {
      selectedFolderPairFilter,
      folderContentsByPair: Object.keys(folderContentsByPair),
      mappingPairs: mappingPairs.length,
      hasSourceFolderContents: !!sourceFolderContents,
      hasDestinationFolderContents: !!destinationFolderContents
    });

    // Get folder contents for selected pair
    let sourceFiles = [];
    let targetFiles = [];
    let sourceProjectId = null;
    let targetProjectId = null;
    let selectedSourceFolderIds = [];
    let targetFolderId = null;
    let hasData = false;

    if (selectedFolderPairFilter !== 'all' && folderContentsByPair[selectedFolderPairFilter]) {
      const pairData = folderContentsByPair[selectedFolderPairFilter];
      console.log(`📂 Extracting data for pair ${selectedFolderPairFilter}:`, {
        hasSource: !!pairData.source,
        hasTarget: !!pairData.target,
        sourceDataLength: pairData.source?.data?.length || 0,
        targetDataLength: pairData.target?.data?.length || 0,
        sourceData: pairData.source?.data,
        targetData: pairData.target?.data
      });

      sourceFiles = pairData.source?.data || pairData.source?.results || [];
      targetFiles = pairData.target?.data || pairData.target?.results || [];
      sourceProjectId = pairData.source?.projectId || null;
      targetProjectId = pairData.target?.projectId || null;
      selectedSourceFolderIds = pairData.source?.selectedFolderIds || [];
      targetFolderId = pairData.target?.targetFolderId || null;
      hasData = sourceFiles.length > 0 || targetFiles.length > 0;

      console.log(`📊 Extracted files:`, {
        sourceFilesCount: sourceFiles.length,
        targetFilesCount: targetFiles.length,
        hasData,
        selectedSourceFolderIds
      });

      // Store selected folder IDs for highlighting (must be set before rendering)
      window.selectedSourceFolderIds = selectedSourceFolderIds;

      // Also try to get from copyMappings if not in pairData
      if (selectedSourceFolderIds.length === 0 && copyMappings && copyMappings.length > 0) {
        const [srcProjId, dstProjId] = selectedFolderPairFilter.split('|');
        const mapping = copyMappings.find(m =>
          m.srcProjectId === srcProjId && m.dstProjectId === dstProjId
        );
        if (mapping && mapping.srcFolderIds && mapping.srcFolderIds.length > 0) {
          selectedSourceFolderIds = mapping.srcFolderIds;
          window.selectedSourceFolderIds = selectedSourceFolderIds;
          console.log(`📋 Updated selected folder IDs from copyMappings:`, selectedSourceFolderIds);
        }
      }
    } else if (sourceFolderContents || destinationFolderContents) {
      // Fallback to legacy data if no pair selected or pair data not available
      sourceFiles = sourceFolderContents?.data || [];
      targetFiles = destinationFolderContents?.data || [];
      sourceProjectId = window.flowParamsMap?.srcProjectId || null;
      targetProjectId = window.flowParamsMap?.dstProjectId || null;
      selectedSourceFolderIds = window.selectedSourceFolderIds || [];
      hasData = sourceFiles.length > 0 || targetFiles.length > 0;
    }

    // Get selected pair info for display
    const selectedPair = mappingPairs.find(p => p.key === selectedFolderPairFilter);
    const pairLabel = selectedPair ? selectedPair.label : 'All Pairs';

    console.log('📁 Files to render:', {
      sourceCount: sourceFiles.length,
      targetCount: targetFiles.length,
      pair: selectedPair?.label || 'All',
      hasData
    });

    // Don't render if no data available and no mappings
    if (!hasData && mappingPairs.length === 0 && !sourceFolderContents && !destinationFolderContents) {
      return null;
    }

    // Get the active mapping for the Compare button
    const mappingForCompare = activeMapping || (selectedFolderPairFilter !== 'all'
      ? copyMappings.find(m => {
        const [srcId, dstId] = selectedFolderPairFilter.split('|');
        return m.srcProjectId === srcId && m.dstProjectId === dstId;
      })
      : null);

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              backgroundColor: '#f8f9fa',
              p: 2,
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                <Description sx={{ color: '#1976d2' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">
                    Project Folder Structure ({sourceFiles.length} source, {targetFiles.length} target)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {selectedFolderPairFilter !== 'all'
                      ? `Showing folder structure for: ${pairLabel}. Selected folders for copying are highlighted.`
                      : mappingPairs.length > 0
                        ? 'Select a mapping pair to view folder structure. Selected folders for copying are highlighted.'
                        : 'Showing folder structure. Selected folders for copying are highlighted.'}
                  </Typography>
                </Box>
              </Box>
              {mappingForCompare && mappingForCompare.aclId && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleCompareMapping(mappingForCompare)}
                  disabled={isComparing}
                  startIcon={isComparing ? <CircularProgress size={16} /> : <SyncAlt />}
                  sx={{ ml: 2 }}
                >
                  {isComparing ? 'Comparing…' : 'Compare'}
                </Button>
              )}
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            {/* Mapping Pair Filter - Hidden when viewing a specific mapping (not 'all') */}
            {mappingPairs.length > 0 && selectedFolderPairFilter === 'all' && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Mapping Pair</InputLabel>
                  <Select
                    value={selectedFolderPairFilter}
                    label="Filter by Mapping Pair"
                    onChange={async (e) => {
                      const pairKey = e.target.value;
                      console.log(`🔄 Pair selection changed to: ${pairKey}`);
                      setSelectedFolderPairFilter(pairKey);

                      // If folder contents not yet loaded for this pair, fetch them
                      if (pairKey !== 'all') {
                        const hasCachedData = folderContentsByPair[pairKey] &&
                          (folderContentsByPair[pairKey].source?.data?.length > 0 ||
                            folderContentsByPair[pairKey].target?.data?.length > 0);

                        console.log(`📦 Checking cached data for ${pairKey}:`, {
                          hasCachedData,
                          cached: !!folderContentsByPair[pairKey],
                          sourceCount: folderContentsByPair[pairKey]?.source?.data?.length || 0,
                          targetCount: folderContentsByPair[pairKey]?.target?.data?.length || 0
                        });

                        if (!hasCachedData) {
                          console.log(`🔄 No cached data, fetching for pair: ${pairKey}`);
                          // Extract project IDs from the pair key (format: "srcProjectId|dstProjectId")
                          const [srcProjectId, dstProjectId] = pairKey.split('|');
                          console.log(`🔍 Looking for mapping with:`, {
                            srcProjectId,
                            dstProjectId,
                            copyMappingsCount: copyMappings?.length || 0,
                            copyMappings: copyMappings
                          });

                          // Try to find mapping in copyMappings first
                          let mapping = copyMappings.find(m => {
                            const matches = m.srcProjectId === srcProjectId && m.dstProjectId === dstProjectId;
                            if (!matches && copyMappings.length > 0) {
                              console.log(`  ⚠️ Mapping doesn't match:`, {
                                mappingSrc: m.srcProjectId,
                                mappingDst: m.dstProjectId,
                                expectedSrc: srcProjectId,
                                expectedDst: dstProjectId,
                                srcMatch: m.srcProjectId === srcProjectId,
                                dstMatch: m.dstProjectId === dstProjectId
                              });
                            }
                            return matches;
                          });

                          // If not found in copyMappings, try to load saved mappings from backend
                          if (!mapping && srcProjectId && dstProjectId) {
                            console.log(`📝 copyMappings not loaded, trying to load saved mappings for this pair...`);
                            try {
                              const savedMappings = await loadSavedMappings(config?.masterFlowId);
                              if (savedMappings && savedMappings.length > 0) {
                                mapping = savedMappings.find(m =>
                                  m.srcProjectId === srcProjectId && m.dstProjectId === dstProjectId
                                );
                                if (mapping) {
                                  console.log(`✅ Found mapping in saved mappings:`, mapping);
                                  // Update copyMappings state with the loaded mappings
                                  setCopyMappings(savedMappings);
                                }
                              }
                            } catch (e) {
                              console.warn('Could not load saved mappings:', e);
                            }

                            // If still not found, create a minimal mapping
                            if (!mapping) {
                              console.log(`📝 Creating temporary mapping from pair key (no saved mappings found)`);
                              mapping = {
                                srcProjectId,
                                dstProjectId,
                                srcFolderIds: [], // Will fetch all folders if not specified
                                dstFolderId: null
                              };
                            }
                          }

                          if (mapping) {
                            const token = localStorage.getItem('token');
                            const autodeskDocsConnector = config?.connectors?.find(c => c.mcName === 'Autodesk Docs');
                            if (autodeskDocsConnector) {
                              console.log(`📥 Fetching folder contents for mapping:`, mapping);
                              await fetchFolderContentsForPair(
                                mapping.srcProjectId,
                                mapping.dstProjectId,
                                mapping.srcFolderIds || [],
                                mapping.dstFolderId || null,
                                token,
                                autodeskDocsConnector
                              );
                            } else {
                              console.error('❌ Autodesk Docs connector not found');
                            }
                          } else {
                            console.error(`❌ Could not create mapping for pair: ${pairKey}`, {
                              extractedSrc: srcProjectId,
                              extractedDst: dstProjectId,
                              availableMappings: copyMappings.map(m => ({
                                src: m.srcProjectId,
                                dst: m.dstProjectId,
                                key: `${m.srcProjectId}|${m.dstProjectId}`
                              }))
                            });
                          }
                        } else {
                          console.log(`✅ Using cached data for pair: ${pairKey}`);
                        }
                      }

                      // Reset expanded folders when changing pairs
                      setExpandedSourceFolders({});
                      setExpandedTargetFolders({});
                    }}
                  >
                    <MenuItem value="all">All Pairs</MenuItem>
                    {mappingPairs.map((pair) => (
                      <MenuItem key={pair.key} value={pair.key}>
                        {pair.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {selectedFolderPairFilter === 'all' && mappingPairs.length > 0 && !hasData ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Please select a mapping pair from the dropdown above to view the folder structure.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {/* Source Folder */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                        Source Folder
                      </Typography>
                      <TableContainer sx={{ maxHeight: 500, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Size</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sourceFiles.length > 0 ? (
                              sourceFiles.map((file, index) => (
                                <FolderRow
                                  key={`source-${file.id}-${index}`}
                                  file={file}
                                  projectId={sourceProjectId}
                                  isSource={true}
                                  level={0}
                                  expandedFolders={expandedSourceFolders}
                                  setExpandedFolders={setExpandedSourceFolders}
                                  fetchSubfolderContents={fetchSubfolderContents}
                                />
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    No files in source folder
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Target Folder */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: '#2e7d32' }}>
                        Target Folder
                      </Typography>
                      <TableContainer sx={{ maxHeight: 500, overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>Size</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {targetFiles.length > 0 ? (
                              targetFiles.map((file, index) => (
                                <FolderRow
                                  key={`target-${file.id}-${index}`}
                                  file={file}
                                  projectId={targetProjectId}
                                  isSource={false}
                                  level={0}
                                  expandedFolders={expandedTargetFolders}
                                  setExpandedFolders={setExpandedTargetFolders}
                                  fetchSubfolderContents={fetchSubfolderContents}
                                />
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    No files in target folder
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const viewModeLabels = {
    mapping: 'Mapping',
    comparison: 'Comparison',
    tracking: 'Tracking Records'
  };

  const renderCopyFlowHeader = () => {
    if (viewMode === 'grid') return null;
    const mappingLabel = activeMapping
      ? (activeMapping.mappingName && activeMapping.mappingName.length > 0
        ? activeMapping.mappingName
        : `${activeMapping.srcProjectName || activeMapping.srcProjectId} > ${activeMapping.dstProjectName || activeMapping.dstProjectId}`)
      : 'New Mapping';
    return (
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Breadcrumbs>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={handleBackToGrid}
            sx={{ cursor: 'pointer' }}
          >
            Saved Mappings
          </Link>
          <Typography color="text.primary">{mappingLabel}</Typography>
          <Typography color="text.primary">{viewModeLabels[viewMode] || 'Details'}</Typography>
        </Breadcrumbs>
        <IconButton size="small" onClick={handleOpenHeaderMenu} disabled={!activeMapping}>
          <MoreVert fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={headerMenuAnchorEl}
          open={Boolean(headerMenuAnchorEl)}
          onClose={handleCloseHeaderMenu}
        >
          <MenuItem onClick={async () => {
            handleCloseHeaderMenu();
            if (activeMapping) {
              await handleRunCopyFilesNow(activeMapping);
            } else {
              toast.error('Select a mapping first');
            }
          }} disabled={isRunning}>
            {isRunning ? 'Running…' : 'Run'}
          </MenuItem>
          <MenuItem onClick={() => handleMappingAction(activeMapping, 'mapping')}>Mapping</MenuItem>
          <MenuItem onClick={() => handleMappingAction(activeMapping, 'comparison')}>Comparison</MenuItem>
          <MenuItem onClick={() => handleMappingAction(activeMapping, 'tracking')}>Tracking Records</MenuItem>
          <MenuItem onClick={handleDeleteActiveMapping}>Delete Mapping</MenuItem>
        </Menu>
      </Box>
    );
  };

  const renderSavedMappingsGrid = () => (
    <RootDiv>
      <ContentDiv>
        <StyledPaper elevation={0} square>
          <HeaderContainer>
            <TitleBar>
              <Title variant="h6">
                Saved Mappings
              </Title>
              <Button variant="contained" onClick={handleCreateNewMapping}>
                Add Mapping
              </Button>
            </TitleBar>
          </HeaderContainer>
          <Box
            sx={{
              flexGrow: 1,
              width: '100%',
              height: '100%',
              overflow: 'hidden',
              paddingLeft: 2,
              paddingRight: 2,
            }}
          >
            {autodeskHubsError && (
              <Alert severity="error" sx={{ my: 2 }} onClose={() => setAutodeskHubsError(null)}>
                {autodeskHubsError}
              </Alert>
            )}
            {copyMappings.length === 0 ? (
              <Box
                sx={{
                  flexGrow: 1,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                }}
              >
                <Alert severity="info">No mappings found. Click &quot;Add Mapping&quot; to create one.</Alert>
              </Box>
            ) : (
              <TableContainer
                sx={{
                  flexGrow: 1,
                  width: '100%',
                  height: '100%',
                  overflow: 'auto',
                }}
              >
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    border: 'none',
                    '& .MuiTableHead-root': {
                      backgroundColor: '#F8F8F8',
                      '& .MuiTableCell-head': {
                        color: '#6B0F2A',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      },
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                      fontSize: '0.8125rem',
                    },
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: 'rgba(139, 159, 232, 0.08)',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Source Project</TableCell>
                      <TableCell>Target Project</TableCell>
                      <TableCell>Folders Selected</TableCell>
                      <TableCell>Last Run</TableCell>
                      <TableCell>Synced</TableCell>
                      <TableCell>Not Synced</TableCell>
                      <TableCell>Failed</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {copyMappings.map((mapping, idx) => {
                      const folderCount = folderCountsByMapping[mapping.aclId] || 0;
                      const hasFolders = folderCount > 0;

                      return (
                        <TableRow key={`${mapping.srcProjectId}-${mapping.dstProjectId}-${idx}`}>
                          <TableCell>{mapping.srcProjectName || mapping.srcProjectId}</TableCell>
                          <TableCell>{mapping.dstProjectName || mapping.dstProjectId}</TableCell>
                          <TableCell>
                            {hasFolders ? (
                              <Chip
                                label={`${folderCount} folder${folderCount !== 1 ? 's' : ''}`}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                label="Not configured"
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const mappingKey = `${mapping.srcProjectId}|${mapping.dstProjectId}`;
                              const stats = mappingStatistics[mappingKey];
                              if (stats && stats.lastRun) {
                                const date = new Date(stats.lastRun);
                                return date.toLocaleString();
                              }
                              return '—';
                            })()}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const mappingKey = `${mapping.srcProjectId}|${mapping.dstProjectId}`;
                              const stats = mappingStatistics[mappingKey];
                              return stats ? stats.synced || 0 : '—';
                            })()}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const mappingKey = `${mapping.srcProjectId}|${mapping.dstProjectId}`;
                              const stats = mappingStatistics[mappingKey];
                              return stats ? stats.notSynced || 0 : '—';
                            })()}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const mappingKey = `${mapping.srcProjectId}|${mapping.dstProjectId}`;
                              const stats = mappingStatistics[mappingKey];
                              return stats ? stats.failed || 0 : '—';
                            })()}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={(event) => handleOpenRowMenu(event, mapping)}>
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </StyledPaper>
      </ContentDiv>
      <Menu
        anchorEl={rowMenuAnchorEl}
        open={Boolean(rowMenuAnchorEl)}
        onClose={handleCloseRowMenu}
      >
        <MenuItem onClick={async () => {
          handleCloseRowMenu();
          if (rowMenuMapping?.aclId) {
            await handleRunCopyFilesNow(rowMenuMapping);
          } else {
            toast.error('Cannot run: mapping ID missing. Please reload mappings.');
          }
        }} disabled={isRunning}>
          {isRunning ? 'Running…' : 'Run'}
        </MenuItem>
        <MenuItem onClick={() => handleMappingAction(rowMenuMapping, 'mapping')}>Mapping</MenuItem>
        <MenuItem onClick={() => handleMappingAction(rowMenuMapping, 'comparison')}>Comparison</MenuItem>
        <MenuItem onClick={() => handleMappingAction(rowMenuMapping, 'tracking')}>Tracking Records</MenuItem>
        <MenuItem onClick={() => {
          handleCloseRowMenu();
          setRunHistoryMapping(rowMenuMapping);
          setRunHistoryOpen(true);
        }}>
          Run History
        </MenuItem>
        <MenuItem onClick={handleDeleteRowMapping}>Delete Mapping</MenuItem>
      </Menu>

      {/* Run History Dialog */}
      <RunHistoryDialog
        open={runHistoryOpen}
        onClose={() => setRunHistoryOpen(false)}
        flowId={config?.masterFlowId}
        srcProjectId={runHistoryMapping?.srcProjectId}
        dstProjectId={runHistoryMapping?.dstProjectId}
        srcProjectName={runHistoryMapping?.srcProjectName}
        dstProjectName={runHistoryMapping?.dstProjectName}
      />
    </RootDiv>
  );

  const renderDataPointContent = () => {
    if (!config) {
      return (
        <Alert severity="warning">
          No configuration data available
        </Alert>
      );
    }

    const vaultItems = vaultData?.results || [];
    const fusionProducts = fusionData?.results || [];
    const unanetProducts = unanetData?.results || [];
    const bomItems = bomData?.results || [];
    const qboCustomers = qboData?.customers || [];
    const autodeskCompanies = autodeskData?.results || [];

    // Group merged data by type
    const vaultUnanetData = mergedData.filter(item => item.type === 'vault-unanet');
    const vaultFusionData = mergedData.filter(item => item.type === 'vault-fusion');
    const vaultBomData = mergedData.filter(item => item.type === 'vault-bom');
    const qboAutodeskData = mergedData.filter(item => item.type === 'qbo-autodesk');
    const qboVendorAutodeskData = mergedData.filter(item => item.type === 'qbo-vendor-autodesk');
    const autodeskProjectQboProjectData = mergedData.filter(item => item.type === 'autodesk-project-qbo-project');
    const autodeskDocsFilesData = mergedData.filter(item => item.type === 'autodesk-docs-files');
    const isCopyFilesFlowView = Boolean(config?.flowName && (config.flowName.toLowerCase().includes('copy') || config.flowName.toLowerCase().includes('file')));

    if (isCopyFilesFlowView) {
      return (
        <>
          {viewMode === 'grid' ? (
            renderSavedMappingsGrid()
          ) : (
            <>
              {renderCopyFlowHeader()}
              {viewMode === 'mapping' && (
                <Accordion defaultExpanded sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Settings sx={{ color: '#1976d2' }} />
                      <Typography>Folder Selection for Copy Files</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Project Mapping Info (Read-only) */}
                    <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="textSecondary">Source Project</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {activeMapping?.srcProjectName || activeMapping?.srcProjectId || 'Unknown'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="textSecondary">Target Project</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {activeMapping?.dstProjectName || activeMapping?.dstProjectId || 'Unknown'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* Folder Selection */}
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              Select Source Folders to Copy
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Choose the folders from the source project that you want to copy to the target project.
                            </Typography>
                          </Box>
                          {loadingFolderMappings && (
                            <CircularProgress size={20} />
                          )}
                        </Box>

                        <Paper variant="outlined" sx={{ maxHeight: 500, overflow: 'auto', p: 1, minHeight: 300 }}>
                          {loadingSourceFolders || loadingFolderMappings ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                              <CircularProgress size={24} />
                              <Typography variant="body2" sx={{ ml: 2 }}>
                                {loadingFolderMappings ? 'Loading saved folder selections...' : 'Loading folders...'}
                              </Typography>
                            </Box>
                          ) : sourceFolders.length === 0 ? (
                            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                              <Typography variant="body2" color="textSecondary">No folders available</Typography>
                            </Box>
                          ) : (
                            sourceFolders.map((folder) => (
                              <FolderTreeItem
                                key={folder.id}
                                folder={folder}
                                level={0}
                                mcId={config.connectors?.find(c => c.mcName === 'Autodesk Docs')?.mcId}
                                projectId={activeMapping?.srcProjectId}
                                parentPath=""
                                parentFolderId={null}
                              />
                            ))
                          )}
                        </Paper>

                        {selectedFoldersForMapping.length > 0 && (
                          <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                              {getTopLevelSelectedFoldersCount(selectedFoldersForMapping)} folder(s) selected
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                              These folders will be copied from the source project to the target project.
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>

                    {/* Save Button */}
                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveFolderMappings}
                        disabled={selectedFoldersForMapping.length === 0 || savingFolderMappings || loadingFolderMappings}
                        size="large"
                      >
                        {savingFolderMappings ? 'Saving...' : 'Save Folder Selection'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleBackToGrid}
                        size="large"
                      >
                        Cancel
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
              {viewMode === 'comparison' && renderFolderContents()}
              {viewMode === 'tracking' && (
                autodeskDocsFilesData.length > 0
                  ? renderAutodeskDocsFiles(autodeskDocsFilesData)
                  : <Alert severity="info" sx={{ mb: 2 }}>No tracking records available for this mapping.</Alert>
              )}
            </>
          )}
        </>
      );
    }

    const vaultFusionOperationData = mergedData.filter(item => item.type === 'vault-fusion-operation');

    return (
      <Box>
        {/* Autodesk Docs Files Table */}
        {autodeskDocsFilesData.length > 0 && renderAutodeskDocsFiles(autodeskDocsFilesData)}

        {/* Comparison Tables for other syncs */}
        {vaultUnanetData.length > 0 && renderVaultToUnanetComparison(vaultUnanetData)}
        {vaultFusionData.length > 0 && renderVaultToFusionComparison(vaultFusionData)}
        {vaultFusionOperationData.length > 0 && renderVaultToFusionOperationComparison(vaultFusionOperationData)}
        {vaultBomData.length > 0 && renderVaultToBomComparison(vaultBomData)}
        {qboAutodeskData.length > 0 && renderQboToAutodeskComparison(qboAutodeskData)}
        {qboVendorAutodeskData.length > 0 && renderQboVendorToAutodeskComparison(qboVendorAutodeskData)}
        {autodeskProjectQboProjectData.length > 0 &&
          renderAutodeskProjectToQboProjectComparison(autodeskProjectQboProjectData)}

        {/* Show message if no data to compare */}
        {mergedData.length === 0 && (
          <Alert severity="info">
            No data available for comparison. Please ensure source and target connectors are configured and have data.
          </Alert>
        )}
      </Box>
    );
  };

  const TargetFolderTreeItem = ({ folder, level = 0, projectId, parentPath = '' }) => {
    const folderId = folder.id;
    const folderName = folder.attributes?.displayName || folder.attributes?.name || folder.id;
    const isExpanded = targetExpandedNodes.includes(folderId);
    const children = targetFolderTree[folderId] || [];
    const isLoading = targetLoadingNodes[folderId];
    const isSelected = selectedTargetFolder === folderId;

    const currentPath = parentPath ? `${parentPath}/${folderName}` : folderName;

    const handleExpand = async () => {
      if (isExpanded) {
        // Collapse immediately
        setTargetExpandedNodes(prev => prev.filter(id => id !== folderId));
      } else {
        // OPTIMISTIC UPDATE: Expand immediately for instant UI feedback
        setTargetExpandedNodes(prev => {
          // Prevent duplicate expansion
          if (prev.includes(folderId)) return prev;
          return [...prev, folderId];
        });

        // Fetch children in the background (non-blocking)
        // fetchTargetFolderChildren now handles duplicate prevention internally
        const mc = config.connectors?.find(c => c.mcName === 'Autodesk Docs');
        if (mc && projectId) {
          fetchTargetFolderChildren(mc.mcId, projectId, folderId).catch(error => {
            console.error('Error fetching target folder children:', error);
            // Optionally collapse on error
            setTargetExpandedNodes(prev => prev.filter(id => id !== folderId));
          });
        }
      }
    };

    const handleSelect = (event, checked) => {
      event.stopPropagation();
      if (checked) {
        setSelectedTargetFolder(folderId);
        setSelectedTargetFolderInfo({ id: folderId, name: folderName, path: currentPath });
      } else {
        setSelectedTargetFolder('');
        setSelectedTargetFolderInfo(null);
      }
    };

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, pl: level * 2, '&:hover': { backgroundColor: 'action.hover' } }}>
          <IconButton size="small" onClick={handleExpand} disabled={isLoading} sx={{ width: 24, height: 24, mr: 0.5 }}>
            {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </IconButton>
          <Checkbox
            checked={isSelected}
            onChange={(e) => handleSelect(e, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ p: 0.5 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, ml: 0.5 }}>
            {isExpanded ? <FolderOpen fontSize="small" color="primary" sx={{ mr: 1 }} /> : <Folder fontSize="small" sx={{ mr: 1 }} />}
            <Typography variant="body2">{folderName}</Typography>
          </Box>
          {isLoading && (<CircularProgress size={16} sx={{ ml: 1 }} />)}
        </Box>
        {isExpanded && children.length > 0 && (
          <Box>
            {children.map((child) => (
              <TargetFolderTreeItem
                key={child.id}
                folder={child}
                level={level + 1}
                projectId={projectId}
                parentPath={currentPath}
              />
            ))}
          </Box>
        )}
        {isExpanded && children.length === 0 && !isLoading && (
          <Box sx={{ pl: level * 2 + 4, py: 0.5 }}>
            <Typography variant="caption" color="textSecondary">No subfolders</Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Main Content */}
      <Box p={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={handleBack}>
            Back to Sync Flow
          </Button>
        </Box>
        {renderDataPointContent()}
      </Box>

      {/* Add Mapping Dialog */}
      <Dialog
        open={addMappingDialogOpen}
        onClose={handleCloseAddMappingDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Mapping</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {autodeskHubsError && (
              <Alert severity="error" onClose={() => setAutodeskHubsError(null)}>{autodeskHubsError}</Alert>
            )}
            {/* Account ID */}
            <FormControl fullWidth required>
              <InputLabel>Account ID *</InputLabel>
              <Select
                value={newMappingAccountId}
                onChange={(e) => {
                  setNewMappingAccountId(e.target.value);
                  setNewMappingSourceProjectId('');
                  setNewMappingTargetProjectId('');
                  setNewMappingSourceProjectName(''); // NEW: Reset source project name
                  setNewMappingTargetProjectName(''); // NEW: Reset target project name
                  setDialogProjects([]);
                }}
                label="Account ID *"
                disabled={loadingHubs || !availableHubs.length}
              >
                {availableHubs.length === 0 ? (
                  <MenuItem value="">
                    {loadingHubs ? 'Loading accounts...' : autodeskHubsError ? 'Could not load accounts — see message above' : 'No accounts available'}
                  </MenuItem>
                ) : (
                  availableHubs.map((hub) => {
                    const hubId = hub.id || hub.hubId || '';
                    const hubName = hub.attributes?.name || hub.name || hubId;
                    return <MenuItem key={hubId} value={hubId}>{hubName} ({hubId})</MenuItem>;
                  })
                )}
              </Select>
            </FormControl>

            {/* Source Project */}
            <FormControl fullWidth required disabled={!newMappingAccountId}>
              <InputLabel>Autodesk Source Project *</InputLabel>
              <Select
                value={newMappingSourceProjectId}
                onChange={(e) => {
                  const projectId = e.target.value;
                  setNewMappingSourceProjectId(projectId);
                  // NEW: Capture project name when project is selected
                  const selectedProject = dialogProjects.find(p => p.id === projectId);
                  if (selectedProject) {
                    const projectName = selectedProject.attributes?.name?.name || selectedProject.attributes?.name || projectId;
                    setNewMappingSourceProjectName(projectName);
                  } else {
                    setNewMappingSourceProjectName('');
                  }
                }}
                label="Autodesk Source Project *"
                disabled={loadingDialogProjects || !newMappingAccountId}
              >
                {dialogProjects.length === 0 ? (
                  <MenuItem value="">{loadingDialogProjects ? 'Loading projects...' : newMappingAccountId ? 'No projects available' : 'Please select Account ID first'}</MenuItem>
                ) : (
                  dialogProjects.map((project) => {
                    const name = project.attributes?.name?.name || project.attributes?.name || project.id;
                    return <MenuItem key={project.id} value={project.id}>{name}</MenuItem>;
                  })
                )}
              </Select>
            </FormControl>

            {/* Target Project */}
            <FormControl fullWidth required disabled={!newMappingAccountId}>
              <InputLabel>Autodesk Target Project *</InputLabel>
              <Select
                value={newMappingTargetProjectId}
                onChange={(e) => {
                  const projectId = e.target.value;
                  setNewMappingTargetProjectId(projectId);
                  // NEW: Capture project name when project is selected
                  const selectedProject = dialogProjects.find(p => p.id === projectId);
                  if (selectedProject) {
                    const projectName = selectedProject.attributes?.name?.name || selectedProject.attributes?.name || projectId;
                    setNewMappingTargetProjectName(projectName);
                  } else {
                    setNewMappingTargetProjectName('');
                  }
                }}
                label="Autodesk Target Project *"
                disabled={loadingDialogProjects || !newMappingAccountId}
              >
                {dialogProjects.length === 0 ? (
                  <MenuItem value="">{loadingDialogProjects ? 'Loading projects...' : newMappingAccountId ? 'No projects available' : 'Please select Account ID first'}</MenuItem>
                ) : (
                  dialogProjects.map((project) => {
                    const name = project.attributes?.name?.name || project.attributes?.name || project.id;
                    return <MenuItem key={project.id} value={project.id}>{name}</MenuItem>;
                  })
                )}
              </Select>
            </FormControl>

            {/* Project Name (optional) */}
            <TextField
              fullWidth
              label="Project Name (optional)"
              value={newMappingProjectName}
              onChange={(e) => setNewMappingProjectName(e.target.value)}
              placeholder="e.g. Warehouse Construction Project"
            />

            {/* Project Number (optional) */}
            <TextField
              fullWidth
              label="Project Number (optional)"
              value={newMappingProjectNumber}
              onChange={(e) => setNewMappingProjectNumber(e.target.value)}
              placeholder="e.g. PROJ-2024-001"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddMappingDialog} disabled={newMappingLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewMapping}
            variant="contained"
            disabled={newMappingLoading || !newMappingAccountId || !newMappingSourceProjectId || !newMappingTargetProjectId}
          >
            {newMappingLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={handleCancelDeleteMapping}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete Mapping</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this mapping?
            </Typography>
            {mappingToDeleteIndex !== null && copyMappings[mappingToDeleteIndex] && (() => {
              const mapping = copyMappings[mappingToDeleteIndex];
              const sourceName = mapping.aclSrcProjectName || mapping.srcProjectName || mapping.srcProjectId || 'Unknown';
              const targetName = mapping.aclDstProjectName || mapping.dstProjectName || mapping.dstProjectId || 'Unknown';
              return (
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Mapping Details:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Source:</strong> {sourceName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Target:</strong> {targetName}
                  </Typography>
                </Box>
              );
            })()}
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Warning:</strong> This action will permanently delete:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
                <li>The project mapping</li>
                <li>All associated folder mappings</li>
                <li>All sync records (file copy logs) for this mapping</li>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                This action cannot be undone.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDeleteMapping}
            disabled={isDeletingMapping}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteMapping}
            disabled={isDeletingMapping}
            variant="contained"
            color="error"
            startIcon={isDeletingMapping ? <CircularProgress size={16} /> : <Delete />}
          >
            {isDeletingMapping ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compare Dialog */}
      <Dialog
        open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Project Comparison</Typography>
            <IconButton onClick={() => setCompareDialogOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isComparing ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Comparing projects...</Typography>
            </Box>
          ) : compareData?.error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {compareData.error}
            </Alert>
          ) : compareData ? (
            <Box sx={{ mt: 2 }}>
              {/* Project Names */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Source Project</Typography>
                      <Typography variant="h6">
                        {compareData.sourceProjectName || compareData.sourceProjectId}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Target Project</Typography>
                      <Typography variant="h6">
                        {compareData.targetProjectName || compareData.targetProjectId}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Comparison Statistics */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Source Project
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Chip
                          label={`${compareData.sourceFolders || 0} Folders`}
                          color="primary"
                          variant="filled"
                          sx={{
                            fontWeight: 600
                          }}
                        />
                        <Chip
                          label={`${compareData.sourceFiles || 0} Files`}
                          color="primary"
                          variant="filled"
                          sx={{
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Target Project
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Chip
                          label={`${compareData.targetFolders || 0} Folders`}
                          color="primary"
                          variant="filled"
                          sx={{
                            fontWeight: 600
                          }}
                        />
                        <Chip
                          label={`${compareData.targetFiles || 0} Files`}
                          color="primary"
                          variant="filled"
                          sx={{
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Failed Items */}
              {compareData.failedItems && compareData.failedItems.length > 0 ? (
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <ErrorIcon color="error" />
                      <Typography variant="h6">
                        Failed Items ({compareData.failedCount || compareData.failedItems.length})
                      </Typography>
                    </Box>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>File Name</strong></TableCell>
                            <TableCell><strong>Path</strong></TableCell>
                            <TableCell><strong>Error</strong></TableCell>
                            <TableCell><strong>Date</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {compareData.failedItems.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{item.fileName || 'N/A'}</TableCell>
                              <TableCell>
                                <Tooltip title={item.filePath || ''}>
                                  <Typography variant="body2" sx={{
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {item.filePath || 'N/A'}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Tooltip title={item.error || ''}>
                                  <Typography variant="body2" sx={{
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    color: 'error.main'
                                  }}>
                                    {item.error || 'N/A'}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                {item.createdAt
                                  ? new Date(item.createdAt).toLocaleString()
                                  : 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              ) : (
                <Alert severity="success" sx={{ mt: 2 }}>
                  No failed items found in the sync log.
                </Alert>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default DataPointDetails;
